// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Linking,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
} from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import * as Location from 'expo-location';
import { Ionicons } from '@expo/vector-icons';
import { Redirect, useLocalSearchParams, useRootNavigationState, useRouter } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';

import { Text, View } from '@/components/Themed';
import { useI18n } from '@/i18n';
import { getSharedBffWebSocket, GolfCourseChangedEvent, GolfCourseUpsertRejectedEvent } from '@/services/bffWebSocket';
import { useUser } from './user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { createRequestId } from '@/services/requestId';
import { useRoundState } from '@/stores/roundState';
import { useManageableCourses, type ManageableCourse } from '@/hooks/useManageableCourses';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import { formatLatLon } from '@/features/course-trace/coordinateInput';
import {
  hasInvalidTeeLength,
  teeLengthsForSave,
  teeLengthsFromStored,
} from '@/features/course-trace/holeTeeLengths';
import { LocationPickerMap } from '@/features/course-trace/LocationPickerMap';
import { mergeImportedHoles } from '@/features/course-trace/osmHoleMerge';
import { formatTravelDistance } from '@/features/units/distance';
import { useDistanceUnit } from '@/stores/distanceUnitState';
import {
  canImport,
  defaultSelection,
  featuresToImport,
  greenPositionsOf,
  hasAnythingToImport,
  hasCoordinate,
  isCategoryAvailable,
  matchCoordinatePair,
  needsGeometry,
  needsTerrain,
  pickSearchAnchor,
  readSearchResponse,
  terrainSkippedNoHoles,
  type OsmImportFeature as OsmImportFeatureShape,
  type OsmSearchAnchor,
  type OsmSearchDiagnostics,
  positionsAreApproximate,
  teePositionsOf,
} from '@/features/course-trace/osmImportSelection';
import appColors, { alpha } from '@/theme/colors';
import { parseDecimalInput } from '@/utils/decimalInput';

const HOLE_COUNT = 18;

type HoleRow = {
  number: number;
  par: string;
  strokeIndex: string;
  lengthMeters: string;
  /**
   * The hole's length from each individual tee, keyed by the tee row's own id — not its name,
   * so renaming a tee keeps the column and the numbers typed into it. Blank/missing means
   * nobody entered one, which is not the same as zero.
   */
  teeLengths: Record<string, string>;
};
type TeeRow = {
  id: string;
  name: string;
  sex: string;
  courseRating: string;
  slopeRating: string;
};

type SaveState = {
  status: 'idle' | 'saving' | 'success' | 'error';
  message?: string | null;
};

type ScanState = {
  status: 'idle' | 'scanning' | 'error';
  message?: string | null;
};

type ScanResponseHole = { number: number; par: number; strokeIndex: number | null };
// `query` is the Overpass QL the service ran, echoed back for the admin debug panel — the
// counts are the first thing to disbelieve when a course looks mapped and imports nothing.
type OsmFeatureCounts = {
  holes: number;
  greens: number;
  tees: number;
  /** Bunker, fairway and water polygons. Absent from a server that predates the terrain import. */
  terrain?: number;
  query?: string | null;
};
type OsmCourseMatch = {
  osmType: string;
  osmId: number;
  name: string;
  /** Where OSM puts the course. Present on every match — it is what the search ranked on. */
  lat?: number;
  lon?: number;
  distanceMeters: number;
  /** "Town, country". Set by a name search, where distance is meaningless. */
  locality?: string | null;
  /** Absent until the course is picked — counting the whole result set timed the search out. */
  counts?: OsmFeatureCounts;
  /**
   * Which server-side lookup produced this candidate: `nominatim`, `nominatim-variant`,
   * `radius-affinity`, `radius-nearest` or `nearby`. `radius-nearest` is the one to notice — it
   * means nothing matched the typed name and these are merely the closest courses.
   */
  matchSource?: string | null;
};
// `lengthMeters` is reported but deliberately not merged into the form — see osmHoleMerge.
// The server stores OSM's measurement against the course as its own source instead.
type OsmHole = { number: number; par: number | null; strokeIndex: number | null; lengthMeters: number | null };
type OsmHolesResponse = {
  holes?: OsmHole[];
  totalPar?: number | null;
  warnings?: string[];
  query?: string | null;
};
// The shape is defined once, next to the rules that filter on it.
type OsmFeatureImport = OsmImportFeatureShape;
/**
 * A hole's centreline, tee first. Carried through the import untouched — nothing here reads
 * it. Its consumer is the imagery analyser, which uses the line to work out which mown grass
 * belongs to which hole, and that runs minutes later on a server.
 */
type OsmHoleCentreline = { holeNumber: number; path: { lat: number; lon: number }[] };
type OsmGeometryResponse = {
  features?: OsmFeatureImport[];
  teeNames?: string[];
  warnings?: string[];
  query?: string | null;
  /** Absent from a server that predates the centreline import. */
  centrelines?: OsmHoleCentreline[];
};
type OsmTerrainResponse = {
  features?: OsmFeatureImport[];
  warnings?: string[];
  query?: string | null;
};

/** Which categories of OSM data the user ticked. */
type OsmSelection = {
  holes: boolean;
  tees: boolean;
  gps: boolean;
  position: boolean;
  terrain: boolean;
};

/** Where an OSM search measured from — the club's stored position, the device's, or a point picked on the map. */
type SearchAnchor = OsmSearchAnchor;

/**
 * One OSM search, kept so an admin can see what was asked and what came back.
 *
 * The search has two server-side paths that produce visibly different rows — a Nominatim hit
 * carries a locality, a radius-fallback match carries none — and nothing in the normal UI says
 * which one answered. The request URL and the raw matches do.
 */
type OsmDebugEntry = {
  id: string;
  at: string;
  /** Which call this was: `search`, `counts`, `holes`, `geometry`, `position`, `features`. */
  label: string;
  mode?: 'nearby' | 'name';
  query?: string | null;
  anchor?: SearchAnchor | null;
  url: string | null;
  /** What became of the request, in diagnostic English — not shown to ordinary users. */
  outcome: string;
  matches?: OsmCourseMatch[];
  /** Null against a geodata-service that predates the diagnostics envelope. */
  diagnostics?: OsmSearchDiagnostics | null;
  /** Free-form lines: server warnings, counts, whatever the call in question returned. */
  detail?: string[];
  method?: 'GET' | 'POST';
  /** Request body for a POST, so the curl below can be run as-is. */
  body?: string;
  /**
   * The Overpass QL the service ran for this call, when it reports one.
   *
   * This is the answer to "OSM clearly has holes, why does the import say it doesn't": the
   * service's claim is about what a query returned, and until you can run that query yourself
   * there is no way to tell a data problem from a query problem.
   */
  overpassQuery?: string | null;
};

/** Where the service sends its Overpass queries, so the curl below reaches the same place. */
const OVERPASS_URL = 'https://overpass-api.de/api/interpreter';

/**
 * The call as a shell command.
 *
 * The token is a `$GOFFY_TOKEN` placeholder rather than the real one: this panel is on screen,
 * in screenshots and over shoulders, and a bearer token pasted into any of those is a bearer
 * token leaked.
 */
const curlForEntry = (entry: OsmDebugEntry): string | null => {
  if (!entry.url) return null;
  const parts = ['curl -sS'];
  if (entry.method === 'POST') parts.push('-X POST', "-H 'Content-Type: application/json'");
  parts.push('-H "Authorization: Bearer $GOFFY_TOKEN"');
  if (entry.body) parts.push(`-d '${entry.body.replace(/'/g, "'\\''")}'`);
  parts.push(`'${entry.url}'`);
  return parts.join(' ');
};

/** The same Overpass query the service ran, as a command that needs no auth at all. */
const curlForOverpass = (query: string): string =>
  `curl -sS '${OVERPASS_URL}' --data-urlencode 'data=${query.replace(/'/g, "'\\''")}'`;

/**
 * Enough history to hold a whole import — search, counts, holes, geometry, writes — and the
 * search before it, without growing unbounded.
 */
const OSM_DEBUG_LOG_LIMIT = 20;

/** The object's own page on openstreetmap.org — where its tags and geometry can be read. */
const osmObjectUrl = (osmType: string, osmId: number) =>
  `https://www.openstreetmap.org/${osmType}/${osmId}`;

/** A marked point on the map, which is how a bare coordinate gets inspected. */
const osmPointUrl = (point: { lat: number; lon: number }) =>
  `https://www.openstreetmap.org/?mlat=${point.lat}&mlon=${point.lon}#map=15/${point.lat}/${point.lon}`;

type OsmState = {
  status: 'idle' | 'searching' | 'importing' | 'error';
  message?: string | null;
  /** Open state of the import modal. Search runs inside it, not before it. */
  modalOpen?: boolean;
  mode?: 'nearby' | 'name';
  matches?: OsmCourseMatch[];
  /**
   * The point the search measured from, kept so each match's distance can be read against a
   * real coordinate instead of an invisible one. `club` is the course's stored position,
   * `device` the phone's — the two answer different questions about a 12 km gap.
   */
  anchor?: SearchAnchor;
  /** The candidate whose categories are being chosen. */
  selected?: OsmCourseMatch;
  selection?: OsmSelection;
  /** The map-picker step is open, in place of the find step. */
  pickerOpen?: boolean;
  /**
   * A point confirmed from the map picker. Checked before the club/device anchor, and cleared
   * every time the modal is reopened — `openOsmModal` replaces the whole state object, so a
   * point picked for one course never bleeds into the next.
   */
  mapAnchor?: { lat: number; lon: number } | null;
  /**
   * Which coordinate "Course position" imports, when the selected match has its own and a
   * picked point differs from it. Defaults to the match's OSM coordinate — the picked point is
   * only what found the match, not a claim that it is more accurate than what OSM holds.
   */
  positionSource?: 'osm' | 'map';
};

const countsOf = (match: OsmCourseMatch): OsmFeatureCounts =>
  match.counts ?? { holes: 0, greens: 0, tees: 0 };
type ScanResponse = {
  holes?: ScanResponseHole[];
  totalPar?: number | null;
  source?: string;
  warnings?: string[];
};

type RoundSummary = {
  courseId?: string;
  CourseId?: string;
  courseName?: string;
  CourseName?: string;
  courseAddress?: string;
  CourseAddress?: string;
};

type GeoCourseDetail = {
  id?: string;
  Id?: string;
  lat?: number;
  Lat?: number;
  lon?: number;
  Lon?: number;
  name?: string;
  Name?: string;
  clubName?: string;
  ClubName?: string;
  city?: string;
  City?: string;
  clubWebsiteUrl?: string | null;
  ClubWebsiteUrl?: string | null;
  visibility?: string;
  Visibility?: string;
  holes?: GeoCourseHole[];
  Holes?: GeoCourseHole[];
  tees?: Array<{
    id?: string;
    Id?: string;
    name?: string;
    Name?: string;
    sex?: string;
    Sex?: string;
    courseRating?: number;
    CourseRating?: number;
    slopeRating?: number;
    SlopeRating?: number;
    par?: number;
    Par?: number;
  }>;
  Tees?: Array<{
    id?: string;
    Id?: string;
    name?: string;
    Name?: string;
    sex?: string;
    Sex?: string;
    courseRating?: number;
    CourseRating?: number;
    slopeRating?: number;
    SlopeRating?: number;
    par?: number;
    Par?: number;
  }>;
};

/** One hole as the course detail reports it, in either casing the services use. */
type GeoCourseHole = {
  number?: number;
  Number?: number;
  par?: number;
  Par?: number;
  strokeIndex?: number;
  StrokeIndex?: number;
  lengthMeters?: number | null;
  LengthMeters?: number | null;
  /** The hole from each individual tee. Absent on a server that predates per-tee lengths. */
  teeLengths?: GeoCourseHoleTeeLength[] | null;
  TeeLengths?: GeoCourseHoleTeeLength[] | null;
};

type GeoCourseHoleTeeLength = {
  teeId?: string;
  TeeId?: string;
  lengthMeters?: number;
  LengthMeters?: number;
};

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const normalizeGuid = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!GUID_REGEX.test(trimmed)) return null;
  return trimmed.toLowerCase();
};

export default function ConfigCourseScreen() {
  const {
    roundId: roundIdParam,
    closeOnSave: closeOnSaveParam,
    manage: manageParam,
    courseId: courseIdParam,
  } = useLocalSearchParams<{
    roundId?: string;
    closeOnSave?: string;
    manage?: string;
    courseId?: string;
  }>();
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const { t } = useI18n();
  const { user } = useUser();
  const storedRoundId = useRoundState((state) => state.roundId);
  const routeRoundId = useMemo(() => (Array.isArray(roundIdParam) ? roundIdParam[0] : roundIdParam) ?? null, [roundIdParam]);
  const closeOnSave = useMemo(() => {
    const value = (Array.isArray(closeOnSaveParam) ? closeOnSaveParam[0] : closeOnSaveParam) ?? '';
    return value === '1' || value === 'true';
  }, [closeOnSaveParam]);
  const effectiveRoundId = routeRoundId ?? storedRoundId;
  // Management mode (opened from the "Golf courses" menu): shows a selector of courses the user may
  // manage so they can pick one to edit, in addition to creating a new one.
  const manageMode = useMemo(() => {
    const value = (Array.isArray(manageParam) ? manageParam[0] : manageParam) ?? '';
    return value === '1' || value === 'true';
  }, [manageParam]);
  // The course and hole a shared/reloaded manage-mode link points at. Only meaningful together —
  // a hole number means nothing without knowing which course's holes it indexes — but read
  // separately since the course loads once (an id) while the hole moves with every page (a
  // number, kept in sync as the admin pages through the mapped-holes browser).
  const courseIdFromUrl = useMemo(
    () => normalizeGuid((Array.isArray(courseIdParam) ? courseIdParam[0] : courseIdParam) ?? null),
    [courseIdParam]
  );
  const { courses: manageableCourses, refresh: refreshManageableCourses } = useManageableCourses(user?.token);
  const [courseSelectorOpen, setCourseSelectorOpen] = useState(false);
  // Free-text filter over the manageable courses. An admin's list runs to hundreds of courses, so
  // scrolling the raw list is not a way to find one.
  const [courseSearch, setCourseSearch] = useState('');
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));
  const pendingRequestIdRef = useRef<string | null>(null);
  // Imported tee/green positions waiting for the course to have an id. Held in a ref rather
  // than state so the save handler reads the current value, not a closed-over stale one.
  const pendingGeometryRef = useRef<OsmFeatureImport[] | null>(null);
  // Staged alongside the positions and written in the same call. Nothing selects them
  // separately: a centreline is a property of the hole the user already chose to import, not
  // another thing to opt into.
  const pendingCentrelinesRef = useRef<OsmHoleCentreline[] | null>(null);
  const flushPendingGeometryRef = useRef<((courseId: string) => Promise<void>) | null>(null);
  const pendingPositionRef = useRef<{ lat: number; lon: number } | null>(null);
  const flushPendingPositionRef = useRef<((courseId: string) => Promise<void>) | null>(null);
  // The OSM element a course was imported from, remembered so the next save records it on the
  // catalogue row — which is what flips the admin's "Not imported from OSM" line. Rides the
  // ordinary golfCourse.upsert rather than a side call, since it is a golf-courses field like
  // the name and holes, not geodata geometry. Cleared when the edited course changes so one
  // course's OSM id can never leak onto another.
  const importedOsmRef = useRef<{ osmType: string; osmId: number } | null>(null);
  const [editingCourseId, setEditingCourseId] = useState<string | null>(null);
  const [hydratingCourse, setHydratingCourse] = useState(false);
  const [clubName, setClubName] = useState('');
  const [city, setCity] = useState('');
  const [clubWebsiteUrl, setClubWebsiteUrl] = useState('');
  const [courseName, setCourseName] = useState('');
  // Purely for display: the club's known position, mirroring clubCoordsRef so a change re-renders.
  // Kept separate from courseCoords, which stays unset unless this session changed the position —
  // that omission is what lets save skip lat/lon instead of overwriting the stored one.
  const [displayCoords, setDisplayCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [holes, setHoles] = useState<HoleRow[]>(
    Array.from({ length: HOLE_COUNT }, (_, idx) => ({
      number: idx + 1,
      par: '4',
      strokeIndex: String(idx + 1),
      lengthMeters: '',
      teeLengths: {},
    }))
  );
  const [tees, setTees] = useState<TeeRow[]>([
    {
      id: `tee-${Date.now()}`,
      name: 'Yellow',
      sex: 'male',
      courseRating: '72.0',
      slopeRating: '113',
    },
  ]);
  const [teePar, setTeePar] = useState('72');
  // Checkbox maps to the visibility enum: checked = official, unchecked = private. (The DB also
  // supports "pending", which is set elsewhere for now.)
  const [isOfficial, setIsOfficial] = useState(true);
  // Disabling retires a course: visibility 'disabled' hides it from every course picker while
  // keeping the record. Only meaningful when managing an existing course.
  const [isDisabled, setIsDisabled] = useState(false);
  // Lets the user save even when the live blocking-issue checks fail (e.g. par sum mismatch or a
  // duplicate stroke index from an imperfect scan).
  const [allowSaveWithErrors, setAllowSaveWithErrors] = useState(false);
  const [genderPickerTeeId, setGenderPickerTeeId] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>({ status: 'idle', message: null });
  const [scanState, setScanState] = useState<ScanState>({ status: 'idle', message: null });
  const [osmState, setOsmState] = useState<OsmState>({ status: 'idle', message: null });
  const [osmNameQuery, setOsmNameQuery] = useState('');
  // Admin-only. Survives the search that follows it, so the panel can be opened once and left
  // open while a name is tried three different ways.
  const [osmDebugOpen, setOsmDebugOpen] = useState(false);
  const [osmDebugLog, setOsmDebugLog] = useState<OsmDebugEntry[]>([]);
  const canCopy = typeof navigator !== 'undefined' && !!navigator.clipboard?.writeText;
  const { isAdmin } = useEffectiveRoles(user);
  // The course's position as the form holds it, so a coordinate imported from OSM reaches the
  // save. Null means "unchanged" — the save omits it and the club keeps whatever it had.
  const [courseCoords, setCourseCoords] = useState<{ lat: number; lon: number } | null>(null);
  const distanceUnit = useDistanceUnit();
  const formatDistance = (metres: number) => formatTravelDistance(metres, distanceUnit);
  /**
   * The coordinate line under an OSM match: the point the search measured from, the point OSM
   * holds, and the distance between them.
   *
   * Falls back to the distance alone when either end is missing — a match with no coordinate
   * cannot be plotted, and there is no anchor until a search has actually run.
   */
  const osmMatchCoordinateLine = (match: OsmCourseMatch): string => {
    const distance = formatDistance(match.distanceMeters);
    const anchor = osmState.anchor;
    const pair = matchCoordinatePair(anchor, match);
    if (!anchor || !pair) return distance;
    return t('courseConfig.osmCoordinateLine', {
      origin: t(
        anchor.source === 'club'
          ? 'courseConfig.osmOriginStored'
          : anchor.source === 'device'
            ? 'courseConfig.osmOriginDevice'
            : 'courseConfig.osmOriginMap'
      ),
      originCoords: pair.origin,
      osmCoords: pair.osm,
      distance,
    });
  };
  // The club's position, captured while hydrating an existing course. Far better than the device's
  // position for an OSM search: the user is usually editing from home, not from the course.
  const clubCoordsRef = useRef<{ lat: number; lon: number } | null>(null);
  // Seconds elapsed while the AI scan is running, so the button can show live progress
  // (the Claude vision call with adaptive thinking can take up to a minute).
  const [scanElapsed, setScanElapsed] = useState(0);
  const [cameraOpen, setCameraOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Tick a live elapsed-seconds counter while a scan is in flight; reset when it ends.
  useEffect(() => {
    if (scanState.status !== 'scanning') {
      setScanElapsed(0);
      return;
    }
    setScanElapsed(0);
    const startedAt = Date.now();
    const timer = setInterval(() => {
      setScanElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [scanState.status]);

  useEffect(() => {
    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    if (isFocused) {
      socket.setSubscriptionContext({ roundId: null, tourId: null });
    }
    const unsubscribe = socket.onMessage((message) => {
      const courseChanged = message as GolfCourseChangedEvent;
      if (courseChanged.type === 'golfCourse.changed') {
        const requestId = courseChanged.requestId ?? courseChanged.RequestId;
        if (!requestId || pendingRequestIdRef.current !== requestId) {
          return;
        }
        const changedCourseId = normalizeGuid(courseChanged.courseId ?? courseChanged.CourseId ?? null);
        if (changedCourseId) {
          setEditingCourseId(changedCourseId);
          // The course now has an id, so any positions staged during an OSM import can be
          // written. This is the only moment a newly created course can receive them.
          // Called through a ref so the socket subscription does not have to re-run every
          // time the access token refreshes.
          void flushPendingGeometryRef.current?.(changedCourseId);
          void flushPendingPositionRef.current?.(changedCourseId);
        }
        pendingRequestIdRef.current = null;
        setSaveState({
          status: 'success',
          message: t('courseConfig.saveSuccess', { id: changedCourseId ?? courseChanged.courseId ?? courseChanged.CourseId ?? '' }),
        });
        // Keep the manage selector in sync so a newly created course appears in the list.
        if (manageMode) {
          refreshManageableCourses();
        }
        if (closeOnSave || manageMode) {
          setTimeout(() => {
            if (typeof router.canGoBack === 'function' && router.canGoBack()) {
              router.back();
            } else {
              router.replace('/');
            }
          }, 150);
        }
        return;
      }
      const rejected = message as GolfCourseUpsertRejectedEvent;
      if (rejected.type === 'golfCourse.upsert.rejected') {
        if (!rejected.requestId || pendingRequestIdRef.current !== rejected.requestId) {
          return;
        }
        pendingRequestIdRef.current = null;
        setSaveState({ status: 'error', message: t('courseConfig.saveRejected') });
      }
    });
    return () => unsubscribe();
  }, [closeOnSave, isFocused, manageMode, refreshManageableCourses, router, t, user?.token]);

  // Applies a fetched course detail onto the form state. Shared by the round-hydration effect and the
  // manage-mode course selector.
  const applyCourseDetail = useCallback((course: GeoCourseDetail) => {
    const detailLat = course.lat ?? course.Lat;
    const detailLon = course.lon ?? course.Lon;
    clubCoordsRef.current =
      typeof detailLat === 'number' && typeof detailLon === 'number' && (detailLat !== 0 || detailLon !== 0)
        ? { lat: detailLat, lon: detailLon }
        : null;
    setDisplayCoords(clubCoordsRef.current);
    setCourseName((course.name ?? course.Name ?? '').trim());
    setClubName((course.clubName ?? course.ClubName ?? '').trim());
    setCity((course.city ?? course.City ?? '').trim());
    setClubWebsiteUrl((course.clubWebsiteUrl ?? course.ClubWebsiteUrl ?? '').trim());

    const nextVisibility = (course.visibility ?? course.Visibility)?.toLowerCase();
    if (nextVisibility === 'official' || nextVisibility === 'private' || nextVisibility === 'pending') {
      setIsOfficial(nextVisibility === 'official');
    }
    setIsDisabled(nextVisibility === 'disabled');

    const incomingHoles = (course.holes ?? course.Holes ?? [])
      .map((hole) => {
        const number = hole.number ?? hole.Number ?? 0;
        const par = hole.par ?? hole.Par ?? 4;
        const strokeIndex = hole.strokeIndex ?? hole.StrokeIndex ?? number;
        const lengthMeters = hole.lengthMeters ?? hole.LengthMeters ?? null;
        // Keyed by tee id, which is also the id the tee rows below are hydrated with — so the
        // per-tee columns line up without matching on a name the user can still change.
        const teeLengths = teeLengthsFromStored(
          (hole.teeLengths ?? hole.TeeLengths ?? []).map((entry) => ({
            teeId: normalizeGuid(entry.teeId ?? entry.TeeId ?? null),
            lengthMeters: entry.lengthMeters ?? entry.LengthMeters ?? null,
          }))
        );
        return {
          number,
          par: String(par),
          strokeIndex: String(strokeIndex),
          lengthMeters: lengthMeters ? String(lengthMeters) : '',
          teeLengths,
        };
      })
      .filter((hole) => hole.number > 0)
      .sort((a, b) => a.number - b.number);
    if (incomingHoles.length > 0) {
      setHoles(incomingHoles);
    }

    const incomingTees = (course.tees ?? course.Tees ?? [])
      .map((tee) => {
        const name = (tee.name ?? tee.Name ?? '').trim();
        const sex = (tee.sex ?? tee.Sex ?? '').trim().toLowerCase();
        const courseRating = tee.courseRating ?? tee.CourseRating;
        const slopeRating = tee.slopeRating ?? tee.SlopeRating;
        const par = tee.par ?? tee.Par;
        return {
          id: normalizeGuid(tee.id ?? tee.Id ?? null) ?? `tee-${Date.now()}-${Math.random().toString(16).slice(2)}`,
          name,
          sex: sex === 'female' || sex === 'unisex' ? sex : 'male',
          courseRating: Number.isFinite(courseRating) ? String(courseRating) : '',
          slopeRating: Number.isFinite(slopeRating) ? String(slopeRating) : '',
          teePar: Number.isFinite(par) ? String(par) : '',
        };
      })
      .filter((tee) => tee.name.length > 0);
    if (incomingTees.length > 0) {
      setTees(
        incomingTees.map((tee) => ({
          id: tee.id,
          name: tee.name,
          sex: tee.sex,
          courseRating: tee.courseRating,
          slopeRating: tee.slopeRating,
        }))
      );
      const firstValidPar = incomingTees.find((tee) => tee.teePar.length > 0)?.teePar;
      if (firstValidPar) {
        setTeePar(firstValidPar);
      }
    }
  }, []);

  // Fetches a course by id and hydrates the form. Used when a course is picked in manage mode.
  const hydrateCourseById = useCallback(async (courseId: string) => {
    if (!user?.token) {
      return;
    }
    setHydratingCourse(true);
    try {
      const courseResponse = await fetch(`${apiBaseUrl}/api/geodata/courses/${courseId}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (!courseResponse.ok) {
        return;
      }
      const course = (await courseResponse.json()) as GeoCourseDetail;
      applyCourseDetail(course);
    } finally {
      setHydratingCourse(false);
    }
  }, [apiBaseUrl, user?.token, applyCourseDetail]);

  // Filters the manageable courses on name, club and city. Every whitespace-separated term must
  // match somewhere, so "falster hotel" finds a course no single term pins down.
  const filteredManageableCourses = useMemo(() => {
    const terms = courseSearch.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (terms.length === 0) {
      return manageableCourses;
    }
    return manageableCourses.filter((course) => {
      const haystack = `${course.name} ${course.clubName} ${course.city}`.toLowerCase();
      return terms.every((term) => haystack.includes(term));
    });
  }, [manageableCourses, courseSearch]);

  // Manage mode: pick an existing course to edit.
  const handleSelectManagedCourse = useCallback((course: ManageableCourse) => {
    setCourseSelectorOpen(false);
    setCourseSearch('');
    importedOsmRef.current = null;
    setEditingCourseId(course.id);
    setClubName(course.clubName);
    setCity(course.city);
    setClubWebsiteUrl(course.clubWebsiteUrl ?? '');
    setCourseName(course.name);
    void hydrateCourseById(course.id);
  }, [hydrateCourseById]);

  // Manage mode: reset the form to create a brand-new course.
  const handleCreateNewCourse = useCallback(() => {
    setCourseSelectorOpen(false);
    setCourseSearch('');
    importedOsmRef.current = null;
    setEditingCourseId(null);
    setClubName('');
    setCity('');
    setClubWebsiteUrl('');
    setCourseName('');
    clubCoordsRef.current = null;
    setDisplayCoords(null);
    setIsOfficial(true);
    setIsDisabled(false);
    setTeePar('72');
    setHoles(Array.from({ length: HOLE_COUNT }, (_, idx) => ({ number: idx + 1, par: '4', strokeIndex: String(idx + 1), lengthMeters: '', teeLengths: {} })));
    setTees([{ id: `tee-${Date.now()}`, name: 'Yellow', sex: 'male', courseRating: '72.0', slopeRating: '113' }]);
    setSaveState({ status: 'idle', message: null });
  }, []);

  useEffect(() => {
    if (!effectiveRoundId || !user?.token || !isNavigationReady) {
      return;
    }

    let cancelled = false;
    const loadRoundCourse = async () => {
      setHydratingCourse(true);
      try {
        const roundResponse = await fetch(`${apiBaseUrl}/api/rounds/${effectiveRoundId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!roundResponse.ok) {
          return;
        }

        const round = (await roundResponse.json()) as RoundSummary;
        const roundCourseName = (round.courseName ?? round.CourseName ?? '').trim();
        const roundCourseAddress = (round.courseAddress ?? round.CourseAddress ?? '').trim();
        const roundCourseId = normalizeGuid(round.courseId ?? round.CourseId ?? null);
        if (cancelled) return;

        if (roundCourseName) {
          setCourseName(roundCourseName);
        }
        if (roundCourseAddress) {
          setClubName(roundCourseAddress);
        }
        if (!roundCourseId) {
          return;
        }

        setEditingCourseId(roundCourseId);
        const courseResponse = await fetch(`${apiBaseUrl}/api/geodata/courses/${roundCourseId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!courseResponse.ok) {
          return;
        }

        const course = (await courseResponse.json()) as GeoCourseDetail;
        if (cancelled) return;

        applyCourseDetail(course);
      } finally {
        if (!cancelled) {
          setHydratingCourse(false);
        }
      }
    };

    void loadRoundCourse();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, effectiveRoundId, isNavigationReady, user?.token, applyCourseDetail]);

  // Manage-mode deep link: a `courseId` in the URL (from a shared or reloaded config-course
  // link) loads that course the same way picking it from the manage-mode list would. The
  // round-hydration effect above only fires when there is a round to look a course up
  // through, which a plain manage-mode link never has.
  // Guarded to consume each URL courseId once: `courseId` stays in the URL after "View mapped
  // holes" sets it (router.setParams never clears it), so without the ref this would re-fire on
  // every subsequent editingCourseId change and snap the form back to that course — making it
  // impossible to pick a different course or start a new one from the manage-mode selector.
  const hydratedCourseIdFromUrlRef = useRef<string | null>(null);
  useEffect(() => {
    if (!courseIdFromUrl || effectiveRoundId || !user?.token || !isNavigationReady) return;
    if (hydratedCourseIdFromUrlRef.current === courseIdFromUrl) return;
    hydratedCourseIdFromUrlRef.current = courseIdFromUrl;
    setEditingCourseId(courseIdFromUrl);
    void hydrateCourseById(courseIdFromUrl);
  }, [courseIdFromUrl, effectiveRoundId, hydrateCourseById, isNavigationReady, user?.token]);

  // Web camera: when the capture modal opens, attach the device camera to the <video> preview and
  // stop all tracks when it closes (or on error/unmount).
  useEffect(() => {
    if (!cameraOpen || Platform.OS !== 'web') {
      return;
    }
    let cancelled = false;
    let activeStream: MediaStream | null = null;
    const start = async () => {
      try {
        if (!navigator?.mediaDevices?.getUserMedia) {
          throw new Error('camera unavailable');
        }
        activeStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: false,
        });
        if (cancelled) {
          activeStream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = activeStream;
        if (videoRef.current) {
          videoRef.current.srcObject = activeStream;
          void videoRef.current.play().catch(() => undefined);
        }
      } catch {
        if (!cancelled) {
          setCameraOpen(false);
          setScanState({ status: 'error', message: t('courseConfig.cameraUnavailable') });
        }
      }
    };
    void start();
    return () => {
      cancelled = true;
      if (activeStream) {
        activeStream.getTracks().forEach((track) => track.stop());
      }
      streamRef.current = null;
    };
  }, [cameraOpen, t]);

  if (!isNavigationReady) return null;
  if (!user) return <Redirect href="/" />;

  const updateHole = (index: number, patch: Partial<HoleRow>) => {
    setHoles((prev) => prev.map((hole, idx) => (idx === index ? { ...hole, ...patch } : hole)));
  };

  /** One hole's length from one tee. Blank clears it — the row is simply not sent on save. */
  const updateHoleTeeLength = (index: number, teeId: string, value: string) => {
    setHoles((prev) =>
      prev.map((hole, idx) =>
        idx === index ? { ...hole, teeLengths: { ...hole.teeLengths, [teeId]: value } } : hole
      )
    );
  };

  const addTee = () => {
    setTees((prev) => [
      ...prev,
      {
        id: `tee-${Date.now()}-${prev.length + 1}`,
        name: '',
        sex: 'male',
        courseRating: '',
        slopeRating: '',
      },
    ]);
  };

  const removeTee = (id: string) => {
    setTees((prev) => prev.filter((tee) => tee.id !== id));
  };

  const updateTee = (id: string, patch: Partial<TeeRow>) => {
    setTees((prev) => prev.map((tee) => (tee.id === id ? { ...tee, ...patch } : tee)));
  };

  const validateHoles = () => {
    const parsed = holes.map((hole) => {
      const lengthMeters = Number.parseInt(hole.lengthMeters, 10);
      return {
        number: hole.number,
        par: Number.parseInt(hole.par, 10),
        strokeIndex: Number.parseInt(hole.strokeIndex, 10),
        // Left out entirely when blank — an unknown length must not be saved as 0.
        ...(Number.isFinite(lengthMeters) && lengthMeters > 0 ? { lengthMeters } : {}),
        // Always sent, even empty: the store reads a missing list as "not stated" and keeps
        // what it holds, so clearing the last per-tee length would otherwise never take.
        teeLengths: teeLengthsForSave(hole.teeLengths, tees),
      };
    });
    if (parsed.some((hole) => !Number.isFinite(hole.par) || hole.par <= 0)) {
      return { ok: false, message: t('courseConfig.invalidPar') };
    }
    if (parsed.some((hole) => !Number.isFinite(hole.strokeIndex) || hole.strokeIndex <= 0)) {
      return { ok: false, message: t('courseConfig.invalidStrokeIndex') };
    }
    if (holes.some((hole) => hole.lengthMeters.trim() !== '' && !(Number.parseInt(hole.lengthMeters, 10) > 0))) {
      return { ok: false, message: t('courseConfig.invalidLength') };
    }
    if (holes.some((hole) => hasInvalidTeeLength(hole.teeLengths))) {
      return { ok: false, message: t('courseConfig.invalidLength') };
    }
    return { ok: true, holes: parsed } as const;
  };

  const validateTees = () => {
    if (tees.length === 0) {
      return { ok: false, message: t('courseConfig.teesRequired') } as const;
    }
    const parsedTeePar = Number.parseInt(teePar, 10);
    if (!Number.isFinite(parsedTeePar) || parsedTeePar <= 0) {
      return { ok: false, message: t('courseConfig.invalidTeePar') } as const;
    }

    // A blank rating means "not known" and is sent as null. That is not the same as a bad one:
    // a tee imported from a published scorecard has a real colour and real distances but no
    // rating, and refusing to save the course until somebody invents one would make every such
    // course uneditable.
    const parsed = tees.map((tee) => ({
      // Sent only when it is the id the server gave us. A tee added in this session carries a
      // synthetic `tee-<timestamp>` id, and the backend rejects an id it does not know rather
      // than creating a row under it — so those must go up without one and be matched by name.
      // With a real id a rename is a rename; without it, it is a delete and a recreate.
      ...(normalizeGuid(tee.id) ? { id: tee.id } : {}),
      name: tee.name.trim(),
      sex: tee.sex.trim().toLowerCase(),
      teeColor: tee.name.trim().toLowerCase(),
      courseRating: tee.courseRating.trim() ? parseDecimalInput(tee.courseRating) : null,
      slopeRating: tee.slopeRating.trim() ? Number.parseInt(tee.slopeRating, 10) : null,
      par: parsedTeePar,
    }));

    if (parsed.some((tee) => !tee.name)) {
      return { ok: false, message: t('courseConfig.teeNameRequired') } as const;
    }
    if (parsed.some((tee) => tee.sex !== 'male' && tee.sex !== 'female' && tee.sex !== 'unisex')) {
      return { ok: false, message: t('courseConfig.teeGenderRequired') } as const;
    }
    if (parsed.some((tee) => tee.courseRating !== null && (!Number.isFinite(tee.courseRating) || tee.courseRating <= 0))) {
      return { ok: false, message: t('courseConfig.invalidCourseRating') } as const;
    }
    if (parsed.some((tee) => tee.slopeRating !== null && (!Number.isFinite(tee.slopeRating) || tee.slopeRating <= 0))) {
      return { ok: false, message: t('courseConfig.invalidSlopeRating') } as const;
    }
    return { ok: true, tees: parsed } as const;
  };

  // Issues that must be fixed before the course can be saved. Surfaced live
  // above the action buttons so the user can correct them as they type.
  const getBlockingIssues = (): string[] => {
    const issues: string[] = [];

    // The sum of every hole's par must match the configured Tee par.
    const parValues = holes.map((hole) => Number.parseInt(hole.par, 10));
    const parsedTeePar = Number.parseInt(teePar, 10);
    if (
      parValues.every((par) => Number.isFinite(par) && par > 0) &&
      Number.isFinite(parsedTeePar) &&
      parsedTeePar > 0
    ) {
      const parSum = parValues.reduce((sum, par) => sum + par, 0);
      if (parSum !== parsedTeePar) {
        issues.push(t('courseConfig.parSumMismatch', { sum: parSum, teePar: parsedTeePar }));
      }
    }

    // Each stroke index must be a number between 1 and 18, used by only one hole.
    const holesByStrokeIndex = new Map<number, number[]>();
    holes.forEach((hole) => {
      const raw = hole.strokeIndex.trim();
      const strokeIndex = Number.parseInt(raw, 10);
      if (!Number.isFinite(strokeIndex) || strokeIndex < 1 || strokeIndex > 18) {
        if (raw !== '') {
          issues.push(t('courseConfig.strokeIndexRange', { hole: hole.number, value: raw }));
        }
        return;
      }
      const list = holesByStrokeIndex.get(strokeIndex) ?? [];
      list.push(hole.number);
      holesByStrokeIndex.set(strokeIndex, list);
    });
    holesByStrokeIndex.forEach((holeNumbers, value) => {
      if (holeNumbers.length > 1) {
        issues.push(t('courseConfig.strokeIndexDuplicate', { value, holes: holeNumbers.join(', ') }));
      }
    });

    return issues;
  };

  const blockingIssues = getBlockingIssues();
  const hasBlockingIssues = blockingIssues.length > 0;

  const onSave = () => {
    if (!clubName.trim()) {
      setSaveState({ status: 'error', message: t('courseConfig.clubRequired') });
      return;
    }
    if (hasBlockingIssues && !allowSaveWithErrors) {
      setSaveState({ status: 'error', message: t('courseConfig.fixBeforeSave') });
      return;
    }
    const validation = validateHoles();
    if (!validation.ok) {
      setSaveState({ status: 'error', message: validation.message });
      return;
    }
    const teeValidation = validateTees();
    if (!teeValidation.ok) {
      setSaveState({ status: 'error', message: teeValidation.message });
      return;
    }

    const requestId = createRequestId();
    const payload = {
      type: 'golfCourse.upsert',
      requestId,
      courseId: editingCourseId ?? undefined,
      source: 'user',
      userId: user.id,
      clubName: clubName.trim(),
      city: city.trim(),
      // Omitted when blank, not sent as ''. Absent means "unchanged" all the way down to
      // GolfPersistenceMapper, so a save from a form that never learned the website cannot clear
      // the one the club already has.
      ...(clubWebsiteUrl.trim() ? { clubWebsiteUrl: clubWebsiteUrl.trim() } : {}),
      courseName: courseName.trim(),
      holes: validation.holes,
      tees: teeValidation.tees,
      // Disabled wins over official/private: a retired course is hidden regardless of officiality.
      visibility: isDisabled ? 'disabled' : isOfficial ? 'official' : 'private',
      // Omitted unless this session actually established a position. Sending one the form
      // never learned is how the club's coordinate used to get overwritten with nothing.
      ...(courseCoords ? { lat: courseCoords.lat, lon: courseCoords.lon } : {}),
      // Only when an OSM course was imported this session. Omitted otherwise so a plain save
      // leaves any link the course already has untouched.
      ...(importedOsmRef.current
        ? { osmType: importedOsmRef.current.osmType, osmId: importedOsmRef.current.osmId }
        : {}),
    };

    const socket = socketRef.current;
    const sent = socket.send(payload);
    pendingRequestIdRef.current = requestId;
    setSaveState({
      status: 'saving',
      message: sent === 'sent' ? t('courseConfig.saving') : t('courseConfig.waitingConnection'),
    });
  };

  // Auto-fill par + stroke index (and tee par) from a scanned scorecard. We never guess: any hole
  // the scan didn't confidently read is left BLANK so the user fills it in, rather than showing a
  // misleading default value.
  const applyScan = (data: ScanResponse) => {
    const scannedHoles = Array.isArray(data.holes) ? data.holes : [];
    if (scannedHoles.length > 0) {
      setHoles((prev) =>
        prev.map((row) => {
          const scanned = scannedHoles.find((hole) => hole.number === row.number);
          const parRead = scanned && Number.isFinite(scanned.par);
          const indexRead = scanned && scanned.strokeIndex != null && Number.isFinite(scanned.strokeIndex);
          return {
            ...row,
            par: parRead ? String(scanned!.par) : '',
            strokeIndex: indexRead ? String(scanned!.strokeIndex) : '',
          };
        })
      );
    }
    if (typeof data.totalPar === 'number' && data.totalPar > 0) {
      setTeePar(String(data.totalPar));
    }
    const warning = data.warnings && data.warnings.length > 0 ? data.warnings[0] : null;
    setScanState({ status: 'idle', message: warning ?? t('courseConfig.scanSuccess') });
  };

  // OpenStreetMap import. Same contract as the scorecard scan — anything OSM does not supply
  // arrives as null and is left blank, because a guessed par is worse than an empty box.
  /**
   * Applies imported hole rows to the form.
   *
   * Returns the count and anything the user should be told, rather than setting the message
   * itself: the import continues after this and ends by writing its own summary, which used to
   * overwrite whatever was said here — so a course whose holes could not be read reported a
   * cheerful "imported 0 holes" and never said why.
   */
  const applyOsmHoles = (data: OsmHolesResponse): { count: number; note: string | null } => {
    const imported = Array.isArray(data.holes) ? data.holes : [];
    const warnings = data.warnings ?? [];
    const countIn = (prefix: string) =>
      Number(warnings.find((w) => w.startsWith(prefix))?.split(':')[1] ?? 0);

    // Hole numbers more than one outline claims — several courses on one site, which is why the
    // server refuses to pick one of them. Worth saying whether the import ended up empty or just
    // short: "16 of 18 holes" with no explanation is the silence this whole function exists to
    // break.
    const ambiguous = warnings.find((w) => w.startsWith('hole_ways_ambiguous_ref:'))?.split(':')[1];

    if (imported.length === 0) {
      // "No holes mapped" is the wrong sentence when the holes are plainly there on osm.org —
      // which is the usual case. Centrelines with no `ref` are invisible to the importer but
      // perfectly visible on the map, so name what is actually wrong: they are unnumbered.
      const unnumbered = countIn('hole_ways_without_ref:');
      if (ambiguous) {
        return { count: 0, note: t('courseConfig.osmHolesAmbiguous', { holes: ambiguous }) };
      }

      return {
        count: 0,
        note:
          unnumbered > 0
            ? t('courseConfig.osmHolesUnnumbered', {
                drawn: countIn('hole_ways_seen:'),
                unnumbered,
              })
            : t('courseConfig.osmNoHoles'),
      };
    }

    // An import fills gaps; it never clears. See mergeImportedHoles for why.
    setHoles((prev) => mergeImportedHoles(prev, imported));

    if (typeof data.totalPar === 'number' && data.totalPar > 0) {
      setTeePar(String(data.totalPar));
    }

    // Warnings are codes, not prose, so they can be translated and counted here.
    const unreadable = warnings.find((w) => w.startsWith('stroke_index_unreadable:'));
    if (ambiguous) {
      return { count: imported.length, note: t('courseConfig.osmHolesAmbiguous', { holes: ambiguous }) };
    }

    return {
      count: imported.length,
      note: unreadable
        ? t('courseConfig.osmPartial', { count: Number(unreadable.split(':')[1]) || 0 })
        : null,
    };
  };

  /**
   * Turns OSM tee names into tee rows.
   *
   * OSM has no course rating or slope, so those stay blank for the user — the import saves
   * the typing, not the trip. Names already present are left alone rather than duplicated.
   */
  const applyOsmTeeNames = (names: string[]) => {
    setTees((prev) => {
      const existing = new Set(
        prev.map((tee) => tee.name.trim().toLowerCase()).filter((name) => name.length > 0)
      );

      // A single blank starter row is scaffolding, not data; the import replaces it.
      const kept = prev.filter((tee) => tee.name.trim().length > 0);
      const added = names
        .filter((name) => !existing.has(name.trim().toLowerCase()))
        .map((name, index) => ({
          id: `tee-osm-${index}-${name}`,
          name,
          sex: 'male',
          courseRating: '',
          slopeRating: '',
        }));

      return [...kept, ...added];
    });
  };

  /**
   * Writes staged tee/green positions once the course has an id.
   *
   * A course being created only gets its id back on save, so geometry cannot be written at
   * import time. Staging also means a course the user abandons never leaves positions behind.
   */
  const flushPendingGeometry = async (courseId: string): Promise<void> => {
    const features = pendingGeometryRef.current;
    const centrelines = pendingCentrelinesRef.current ?? [];
    if (!features || features.length === 0 || !user?.token) return;
    pendingGeometryRef.current = null;
    pendingCentrelinesRef.current = null;

    const url = `${apiBaseUrl}/api/geodata/courses/${courseId}/features/import`;
    // The picked OSM course rides along so geodata can refresh from it later.
    const payload = JSON.stringify({
      features,
      centrelines,
      ...(importedOsmRef.current
        ? { osmType: importedOsmRef.current.osmType, osmId: importedOsmRef.current.osmId }
        : {}),
    });
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
        body: payload,
      });
      logOsmCall({
        label: 'features/import',
        url,
        method: 'POST',
        body: payload,
        outcome: response.ok ? 'ok' : `HTTP ${response.status} ${response.statusText}`.trim(),
        detail: [
          `${features.length} feature(s) written · ` +
            `${features.filter((f) => f.kind === 'green').length} green, ` +
            `${features.filter((f) => f.kind === 'tee').length} tee, ` +
            `${features.filter((f) => f.polygon).length} boundary, ` +
            `${centrelines.length} centreline`,
        ],
      });
    } catch (error) {
      // The scorecard has already saved; losing the positions is recoverable by re-importing,
      // so this must not present as a failed save.
      console.warn('[osm] geometry import failed', error);
      logOsmCall({
        label: 'features/import',
        url,
        outcome: `request failed: ${error instanceof Error ? error.message : String(error)}`,
      });
    }
  };

  flushPendingGeometryRef.current = flushPendingGeometry;

  /**
   * Writes the imported course position once the course has an id.
   *
   * Staged like the geometry, and for the same reason. The position also goes into the save
   * payload, which is what gives the club its coordinate — this call is the other half: it
   * tells geodata where to point the imagery analyser, which is the whole reason a coordinate
   * on its own is worth importing.
   */
  const flushPendingPosition = async (courseId: string): Promise<void> => {
    const position = pendingPositionRef.current;
    if (!position || !user?.token) return;
    pendingPositionRef.current = null;

    const url = `${apiBaseUrl}/api/geodata/courses/${courseId}/position`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
        body: JSON.stringify({
          lat: position.lat,
          lon: position.lon,
          name: courseName.trim() || null,
          requestAnalysis: true,
        }),
      });
      logOsmCall({
        label: 'position',
        url,
        method: 'POST',
        body: JSON.stringify({
          lat: position.lat,
          lon: position.lon,
          name: courseName.trim() || null,
          requestAnalysis: true,
        }),
        outcome: response.ok ? 'ok' : `HTTP ${response.status} ${response.statusText}`.trim(),
        detail: [`${formatLatLon(position)} · analysis requested`],
      });
    } catch (error) {
      // The course itself has saved. A missing analysis request is recoverable from the
      // course page's Analyse button, so it must not present as a failed save.
      console.warn('[osm] position import failed', error);
      logOsmCall({
        label: 'position',
        url,
        outcome: `request failed: ${error instanceof Error ? error.message : String(error)}`,
      });
    }
  };

  flushPendingPositionRef.current = flushPendingPosition;

  // Where to search. A point picked on the map wins outright — it exists only because an
  // earlier anchor was wrong. Otherwise the course's own stored position, which is far more
  // reliable than the device's — the user is usually editing from home, not from the course.
  const resolveSearchCoords = async (anchorOverride?: SearchAnchor | null): Promise<SearchAnchor | null> => {
    if (anchorOverride) return anchorOverride;

    const direct = pickSearchAnchor(osmState.mapAnchor, clubCoordsRef.current, null);
    if (direct) return direct;

    // New course, so there is no club or map anchor yet — fall back to where the device is.
    try {
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted') return null;
      const position = await Location.getCurrentPositionAsync({});
      return { lat: position.coords.latitude, lon: position.coords.longitude, source: 'device' };
    } catch {
      return null;
    }
  };

  /** Newest first, because the call just made is the one being read. */
  const logOsmCall = (entry: Omit<OsmDebugEntry, 'id' | 'at'>) => {
    const now = new Date();
    setOsmDebugLog((prev) =>
      [
        { ...entry, id: `${now.getTime()}-${prev.length}`, at: now.toLocaleTimeString() },
        ...prev,
      ].slice(0, OSM_DEBUG_LOG_LIMIT)
    );
  };

  /**
   * Runs one OSM call and logs it, whatever it returns.
   *
   * Every leg of an import goes through here so the panel shows the sequence rather than only
   * the step that failed — "counts said 18 greens, holes returned none" is the shape of the
   * bug, and neither half says it alone.
   */
  const fetchOsmLogged = async <T extends { query?: string | null }>(
    label: string,
    url: string,
    summarize: (body: T) => string[]
  ): Promise<T | null> => {
    if (!user?.token) return null;
    try {
      const response = await fetch(url, { headers: { Authorization: `Bearer ${user.token}` } });
      if (!response.ok) {
        logOsmCall({
          label,
          url,
          outcome: `HTTP ${response.status} ${response.statusText}`.trim(),
        });
        return null;
      }
      const body = (await response.json()) as T;
      logOsmCall({
        label,
        url,
        outcome: 'ok',
        detail: summarize(body),
        overpassQuery: body?.query ?? null,
      });
      return body;
    } catch (error) {
      logOsmCall({
        label,
        url,
        outcome: `request failed: ${error instanceof Error ? error.message : String(error)}`,
      });
      return null;
    }
  };

  /**
   * A match picked on the map with no OSM course entity behind it (`osmType: 'point'`) has no
   * `{osmType}/{osmId}` to query — its data leg goes to the point-anchored fallback routes
   * instead, keyed on the coordinate itself rather than an OSM id.
   */
  const osmSubUrl = (match: OsmCourseMatch, path: 'counts' | 'holes' | 'geometry' | 'terrain') =>
    match.osmType === 'point'
      ? `${apiBaseUrl}/api/geodata/osm/courses/near/${path}?lat=${match.lat}&lon=${match.lon}`
      : `${apiBaseUrl}/api/geodata/osm/courses/${match.osmType}/${match.osmId}/${path}`;

  const searchOsmCourses = async (
    mode: 'nearby' | 'name',
    query = '',
    anchorOverride?: SearchAnchor | null
  ) => {
    if (!user?.token) return;
    const searchName = mode === 'name' ? query.trim() : null;
    if (mode === 'name' && query.trim().length < 3) {
      // A one- or two-letter regex over a few hundred km is what makes Overpass time out.
      setOsmState((prev) => ({ ...prev, status: 'error', message: t('courseConfig.osmNameTooShort') }));
      return;
    }

    setOsmState((prev) => ({
      ...prev,
      status: 'searching',
      message: null,
      mode,
      matches: undefined,
      selected: undefined,
      anchor: undefined,
    }));

    // Both modes are anchored on a position: an unbounded name search times out upstream.
    const coords = await resolveSearchCoords(anchorOverride);
    if (!coords) {
      logOsmCall({
        label: 'search',
        mode,
        query: searchName,
        anchor: null,
        url: null,
        outcome: 'no anchor: no stored club position and no device fix',
        matches: [],
        diagnostics: null,
      });
      setOsmState((prev) => ({ ...prev, status: 'error', message: t('courseConfig.osmNoLocation') }));
      return;
    }

    const url =
      `${apiBaseUrl}/api/geodata/osm/courses?lat=${coords.lat}&lon=${coords.lon}` +
      (mode === 'name' ? `&name=${encodeURIComponent(query.trim())}` : '');

    try {
      const response = await fetch(url, { headers: { Authorization: `Bearer ${user.token}` } });
      if (!response.ok) {
        logOsmCall({
          label: 'search',
          mode,
          query: searchName,
          anchor: coords,
          url,
          outcome: `HTTP ${response.status} ${response.statusText}`.trim(),
          matches: [],
          diagnostics: null,
        });
        setOsmState((prev) => ({ ...prev, status: 'error', message: t('courseConfig.osmFailed') }));
        return;
      }

      // The search deliberately does not carry counts — they cost an area lookup per feature
      // kind per candidate, which timed the endpoint out. They arrive when a course is picked.
      const { matches, diagnostics } = readSearchResponse<OsmCourseMatch>(await response.json());

      logOsmCall({
        label: 'search',
        mode,
        query: searchName,
        anchor: coords,
        url,
        outcome: `${matches.length} match(es)`,
        matches,
        diagnostics,
      });

      if (matches.length === 0) {
        setOsmState((prev) => ({ ...prev, status: 'error', message: t('courseConfig.osmNoCourses') }));
        return;
      }

      // The anchor lands with the matches it produced, so the coordinates a row shows are
      // always the pair the distance on that row was measured between.
      setOsmState((prev) => ({ ...prev, status: 'idle', message: null, matches, anchor: coords }));
    } catch (error) {
      console.warn('[osm] course search failed', error);
      logOsmCall({
        label: 'search',
        mode,
        query: searchName,
        anchor: coords,
        url,
        outcome: `request failed: ${error instanceof Error ? error.message : String(error)}`,
        matches: [],
        diagnostics: null,
      });
      setOsmState((prev) => ({ ...prev, status: 'error', message: t('courseConfig.osmFailed') }));
    }
  };

  // Picking a candidate moves to the category step. Everything it holds is ticked by
  // default; anything it does not hold cannot be ticked at all.
  // Picking a course is what triggers the count lookup — three area lookups for one course,
  // rather than three per candidate across the whole result set.
  const selectOsmCandidate = async (match: OsmCourseMatch) => {
    if (!user?.token) return;
    setOsmState((prev) => ({
      ...prev,
      status: 'searching',
      message: null,
      selected: match,
      positionSource: 'osm',
    }));

    try {
      const counts = await fetchOsmLogged<OsmFeatureCounts>(
        `counts ${match.osmType}/${match.osmId}`,
        `${apiBaseUrl}/api/geodata/osm/courses/${match.osmType}/${match.osmId}/counts`,
        (body) => [
          `holes ${body.holes} · greens ${body.greens} · tees ${body.tees}` +
            ` → green positions ${greenPositionsOf(body)} · tee positions ${teePositionsOf(body)}`,
        ]
      );
      if (!counts) {
        setOsmState((prev) => ({
          ...prev,
          status: 'error',
          selected: undefined,
          message: t('courseConfig.osmFailed'),
        }));
        return;
      }

      const coordinateKnown = hasCoordinate(match);
      if (!hasAnythingToImport(counts, coordinateKnown)) {
        // Mapped as an outline with nothing inside it, and without even a position. Say so
        // rather than opening a step where every option is disabled.
        setOsmState((prev) => ({
          ...prev,
          status: 'error',
          selected: undefined,
          message: t('courseConfig.osmNothingToImport'),
        }));
        return;
      }

      const withCounts = { ...match, counts };
      setOsmState((prev) => ({
        ...prev,
        status: 'idle',
        message: null,
        selected: withCounts,
        selection: defaultSelection(counts, coordinateKnown),
      }));
    } catch (error) {
      console.warn('[osm] count lookup failed', error);
      setOsmState((prev) => ({
        ...prev,
        status: 'error',
        selected: undefined,
        message: t('courseConfig.osmFailed'),
      }));
    }
  };

  /**
   * Opens the import modal, seeded from whatever the user has already typed.
   *
   * If the course is named, that name is almost certainly what they want to look up, so the
   * modal opens on the name tab with it filled in. The club name is the fallback because OSM
   * frequently names the club rather than the individual course ("Österlens golfklubb" covers
   * both Djupadal and Lilla Vik). With neither, there is nothing to search on but position.
   */
  const openOsmModal = () => {
    const prefill = courseName.trim() || clubName.trim();
    setOsmNameQuery(prefill);
    setOsmState({
      status: 'idle',
      message: null,
      modalOpen: true,
      mode: prefill ? 'name' : 'nearby',
    });
  };

  const closeOsmModal = () => {
    setOsmState((prev) => ({
      ...prev,
      modalOpen: false,
      matches: undefined,
      selected: undefined,
      pickerOpen: false,
    }));
  };

  const openLocationPicker = () =>
    setOsmState((prev) => ({ ...prev, pickerOpen: true, message: null }));

  const cancelLocationPicker = () => setOsmState((prev) => ({ ...prev, pickerOpen: false }));

  /**
   * A point confirmed from the map picker becomes the search anchor, and the search that
   * failed with the old anchor runs again immediately with the new one — whatever mode and
   * name query were already in play, since the point has no name of its own to search by. The
   * anchor is passed straight to `searchOsmCourses` rather than left for `resolveSearchCoords`
   * to pick up from state: `setOsmState` here has not committed yet when the search starts.
   */
  const confirmLocationPicker = (point: { lat: number; lon: number }) => {
    setOsmState((prev) => ({ ...prev, pickerOpen: false, mapAnchor: point }));
    void searchOsmCourses(osmState.mode ?? 'nearby', osmNameQuery, { ...point, source: 'map' });
  };

  /**
   * Takes the picked point itself — for when the search at that point found no
   * `leisure=golf_course` way or relation to pick from.
   *
   * That is not the same as OSM having nothing there: individual `golf=hole`/`green`/`tee`
   * ways can and do exist without ever being wrapped in a course boundary, so before settling
   * for position-only this makes one more attempt — a tight-radius count anchored on the exact
   * point, with no course entity to query an area of. Only once that also comes back empty does
   * the category step fall back to Position alone.
   */
  const selectPickedLocation = async (point: { lat: number; lon: number }) => {
    const match: OsmCourseMatch = {
      osmType: 'point',
      osmId: 0,
      name: courseName.trim() || clubName.trim(),
      lat: point.lat,
      lon: point.lon,
      distanceMeters: 0,
    };
    setOsmState((prev) => ({
      ...prev,
      status: 'searching',
      message: null,
      pickerOpen: false,
      mapAnchor: point,
      positionSource: 'osm',
      selected: match,
      selection: undefined,
    }));

    const counts = (await fetchOsmLogged<OsmFeatureCounts>(
      'counts near point',
      osmSubUrl(match, 'counts'),
      (body) => [
        `holes ${body.holes} · greens ${body.greens} · tees ${body.tees} · terrain ${body.terrain ?? 0}`,
      ]
    )) ?? { holes: 0, greens: 0, tees: 0, terrain: 0 };

    setOsmState((prev) => ({
      ...prev,
      status: 'idle',
      message: null,
      selected: { ...match, counts },
      selection: defaultSelection(counts, true),
    }));
  };

  /**
   * Step two: what to take from the chosen course.
   *
   * Categories the course has nothing for are shown with their zero count and disabled — the
   * user can see why an option is unavailable, where hiding it would just look broken.
   */
  const renderOsmCategoryStep = (match: OsmCourseMatch) => {
    // Counts arrive a beat after the course is picked. Say so, rather than rendering the
    // categories as zeroed and disabled — that reads as "this course has nothing", which is
    // the opposite of what is happening.
    if (!match.counts) {
      return (
        <>
          <Text style={styles.pickerOptionText}>{match.name || t('courseConfig.osmUnnamed')}</Text>
          <View style={styles.osmLoadingRow} testID="course-osm-loading">
            <ActivityIndicator color={appColors.text.primary} />
            <Text style={styles.osmDetailText}>{t('courseConfig.osmLoadingCounts')}</Text>
          </View>
          <View style={styles.osmActionRow}>
            <Pressable
              testID="course-osm-back"
              style={styles.osmSecondaryButton}
              onPress={() => setOsmState((prev) => ({ ...prev, selected: undefined, message: null }))}
            >
              <Text style={styles.scanButtonText}>{t('courseConfig.osmBack')}</Text>
            </Pressable>
          </View>
        </>
      );
    }

    const counts = countsOf(match);
    const selection = osmState.selection ?? {
      holes: false,
      tees: false,
      gps: false,
      position: false,
      terrain: false,
    };
    const busy = osmState.status === 'importing';
    const coordinateKnown = hasCoordinate(match);
    // Offered only when there is a real choice: a point was picked on the map, this course
    // carries its own OSM coordinate, and the two disagree by more than rounding — a match
    // found by "use this exact position" already sets match.lat/lon to the picked point, so
    // its own distance is 0 and there is nothing to choose between.
    const showPositionChoice =
      osmState.mapAnchor != null && coordinateKnown && match.distanceMeters > 1;
    const positionSource = osmState.positionSource ?? 'osm';

    const categories: { key: keyof OsmSelection; label: string; detail: string; available: boolean }[] = [
      {
        key: 'holes',
        label: t('courseConfig.osmCategoryHoles'),
        detail: t('courseConfig.osmCategoryHolesDetail', { count: counts.holes }),
        available: isCategoryAvailable(counts, 'holes'),
      },
      {
        key: 'tees',
        label: t('courseConfig.osmCategoryTees'),
        detail: t('courseConfig.osmCategoryTeesDetail', { count: counts.tees }),
        available: isCategoryAvailable(counts, 'tees'),
      },
      {
        key: 'gps',
        label: t('courseConfig.osmCategoryGps'),
        detail: positionsAreApproximate(counts)
          ? t('courseConfig.osmCategoryGpsApproximate', {
              greens: greenPositionsOf(counts),
              tees: teePositionsOf(counts),
            })
          : t('courseConfig.osmCategoryGpsDetail', {
              greens: greenPositionsOf(counts),
              tees: teePositionsOf(counts),
            }),
        available: isCategoryAvailable(counts, 'gps'),
      },
      {
        key: 'terrain',
        // Above Position because it is data rather than a starting point, and below GPS
        // because it is worth nothing without the holes GPS and Holes provide.
        label: t('courseConfig.osmCategoryTerrain'),
        detail: t('courseConfig.osmCategoryTerrainDetail', { count: counts.terrain ?? 0 }),
        available: isCategoryAvailable(counts, 'terrain'),
      },
      {
        key: 'position',
        label: t('courseConfig.osmCategoryPosition'),
        // Last in the list and described by what it unlocks rather than by what it is: on its
        // own a coordinate sounds like nothing, and for a course OSM has not mapped it is the
        // difference between no geometry ever and a green found from the air.
        detail: t('courseConfig.osmCategoryPositionDetail'),
        available: isCategoryAvailable(counts, 'position', coordinateKnown),
      },
    ];

    const nothingSelected = !canImport(selection);

    return (
      <>
        <Text style={styles.pickerOptionText}>{match.name || t('courseConfig.osmUnnamed')}</Text>
        <Text style={styles.manageCourseMeta}>{t('courseConfig.osmChooseData')}</Text>

        {categories.map((category) => (
          <Fragment key={category.key}>
            <Pressable
              testID={`course-osm-category-${category.key}`}
              accessibilityRole="checkbox"
              aria-checked={category.available && selection[category.key]}
              aria-disabled={!category.available}
              disabled={!category.available || busy}
              style={[styles.osmCategoryRow, !category.available && styles.osmCategoryRowDisabled]}
              onPress={() => toggleOsmCategory(category.key)}
            >
              <Ionicons
                name={category.available && selection[category.key] ? 'checkbox' : 'square-outline'}
                size={20}
                color={appColors.text.primary}
              />
              <View style={styles.osmCategoryText}>
                <Text style={styles.pickerOptionText}>{category.label}</Text>
                <Text style={styles.osmDetailText}>{category.detail}</Text>
              </View>
            </Pressable>

            {/* Which coordinate "Course position" actually writes, when there is a real choice
                between them — the OSM entity's own point, or the one that was picked to find
                it, which the search's own distance line already showed disagreeing by enough
                to matter. */}
            {category.key === 'position' && category.available && showPositionChoice ? (
              <View style={styles.osmPositionChoiceRow} testID="course-osm-position-choice">
                <Pressable
                  testID="course-osm-position-osm"
                  accessibilityRole="radio"
                  aria-checked={positionSource === 'osm'}
                  disabled={busy}
                  style={[
                    styles.osmPositionChoiceChip,
                    positionSource === 'osm' && styles.osmPositionChoiceChipActive,
                  ]}
                  onPress={() => setOsmState((prev) => ({ ...prev, positionSource: 'osm' }))}
                >
                  <Text style={styles.osmPositionChoiceText}>
                    {t('courseConfig.osmPositionUseOsm', {
                      distance: formatDistance(match.distanceMeters),
                    })}
                  </Text>
                </Pressable>
                <Pressable
                  testID="course-osm-position-map"
                  accessibilityRole="radio"
                  aria-checked={positionSource === 'map'}
                  disabled={busy}
                  style={[
                    styles.osmPositionChoiceChip,
                    positionSource === 'map' && styles.osmPositionChoiceChipActive,
                  ]}
                  onPress={() => setOsmState((prev) => ({ ...prev, positionSource: 'map' }))}
                >
                  <Text style={styles.osmPositionChoiceText}>
                    {t('courseConfig.osmPositionUsePicked')}
                  </Text>
                </Pressable>
              </View>
            ) : null}
          </Fragment>
        ))}

        {osmState.message ? (
          <Text
            testID="course-osm-modal-status"
            style={osmState.status === 'error' ? styles.errorText : styles.statusText}
          >
            {osmState.message}
          </Text>
        ) : null}

        <View style={styles.osmActionRow}>
          <Pressable
            testID="course-osm-back"
            style={styles.osmSecondaryButton}
            disabled={busy}
            onPress={() => setOsmState((prev) => ({ ...prev, selected: undefined, message: null }))}
          >
            <Text style={styles.scanButtonText}>{t('courseConfig.osmBack')}</Text>
          </Pressable>
          <Pressable
            testID="course-osm-confirm"
            style={[
              styles.scanButton,
              styles.osmPrimaryButton,
              (nothingSelected || busy) && styles.actionButtonDisabled,
            ]}
            disabled={nothingSelected || busy}
            onPress={async () => {
              // The rest of the import — holes, geometry, terrain — still comes from the real
              // OSM entity via match.osmType/osmId, untouched; only the coordinate written for
              // "Course position" is swapped for the picked point when that was chosen.
              const matchToImport =
                positionSource === 'map' && osmState.mapAnchor
                  ? { ...match, lat: osmState.mapAnchor.lat, lon: osmState.mapAnchor.lon }
                  : match;
              await importOsmCourse(matchToImport, selection);
              closeOsmModal();
            }}
          >
            {busy ? (
              <ActivityIndicator color={appColors.text.primary} />
            ) : (
              <Ionicons name="download" size={18} color={appColors.text.primary} />
            )}
            <Text style={styles.scanButtonText}>
              {busy ? t('courseConfig.osmImporting') : t('courseConfig.osmImportAction')}
            </Text>
          </Pressable>
        </View>
      </>
    );
  };

  /**
   * The admin debug panel: every OSM call this screen has made, and how to repeat it.
   *
   * Rendered in two places — inside the import modal, and on the screen beside the import
   * status — because the modal closes on a finished import, taking the explanation of what went
   * wrong with it. `place` only keeps the two testIDs apart.
   */
  const renderOsmDebugPanel = (place: 'modal' | 'screen') => {
    if (!isAdmin) return null;

    return (
      <>
        <Pressable
          testID={`course-osm-debug-toggle-${place}`}
          accessibilityRole="button"
          style={styles.osmDebugToggle}
          onPress={() => setOsmDebugOpen((open) => !open)}
        >
          <Ionicons
            name={osmDebugOpen ? 'chevron-down' : 'chevron-forward'}
            size={14}
            color={appColors.text.muted}
          />
          <Text style={styles.osmDebugToggleText}>
            {t('courseConfig.osmDebugToggle', { count: osmDebugLog.length })}
          </Text>
        </Pressable>

        {osmDebugOpen ? (
          <ScrollView
            testID={`course-osm-debug-panel-${place}`}
            style={styles.osmDebugPanel}
            keyboardShouldPersistTaps="handled"
          >
            {osmDebugLog.length === 0 ? (
              <Text style={styles.osmDebugText}>{t('courseConfig.osmDebugEmpty')}</Text>
            ) : (
              <>
                <Text style={styles.osmDebugText}>{t('courseConfig.osmDebugCurlHint')}</Text>
                {osmDebugLog.map((entry) => {
                  const anchor = entry.anchor;
                  const curl = curlForEntry(entry);
                  return (
                    <View key={entry.id} style={styles.osmDebugEntry}>
                      <Text style={styles.osmDebugHeading}>
                        {`${entry.at} · ${entry.label}` +
                          `${entry.mode ? ` ${entry.mode}` : ''}` +
                          `${entry.query ? ` "${entry.query}"` : ''} · ${entry.outcome}`}
                      </Text>
                      {(entry.detail ?? []).map((line, index) => (
                        <Text key={`${entry.id}-detail-${index}`} style={styles.osmDebugText} selectable>
                          {line}
                        </Text>
                      ))}
                      {anchor ? (
                        <Pressable
                          accessibilityRole="link"
                          onPress={() => void Linking.openURL(osmPointUrl(anchor))}
                        >
                          <Text style={styles.osmDebugLink}>
                            {/* eslint-disable-next-line i18n/no-literal-text -- OSM search diagnostics for admins */}
                            {`from ${anchor.source} ${formatLatLon(anchor)} ↗`}
                          </Text>
                        </Pressable>
                      ) : null}
                      {/* What the server did. `path` is the answer to "why does this list look
                          like that": radius-nearest in particular means the name matched
                          nothing and these are only the closest courses. */}
                      {entry.diagnostics ? (
                        <Text style={styles.osmDebugText} selectable>
                          {`path ${entry.diagnostics.path} · anchor ${entry.diagnostics.anchorSource} ` +
                            `${formatLatLon({ lat: entry.diagnostics.anchorLat, lon: entry.diagnostics.anchorLon })}` +
                            `\nnominatim ${entry.diagnostics.nominatimResults} result(s) · ` +
                            `radius ${entry.diagnostics.radiusCandidates} candidate(s) · ` +
                            `variants ${entry.diagnostics.variantsTried.length > 0 ? entry.diagnostics.variantsTried.join(', ') : 'none'}`}
                        </Text>
                      ) : entry.label === 'search' && entry.url ? (
                        // Only the search carries diagnostics. Saying this on a counts or holes
                        // call would read as a fault where none exists.
                        // eslint-disable-next-line i18n/no-literal-text -- OSM search diagnostics for admins
                        <Text style={styles.osmDebugText}>no diagnostics (older geodata-service)</Text>
                      ) : null}
                      {(entry.matches ?? []).map((match) => (
                        <Pressable
                          key={`${entry.id}-${match.osmType}-${match.osmId}`}
                          accessibilityRole="link"
                          testID={`course-osm-debug-link-${match.osmId}`}
                          onPress={() => void Linking.openURL(osmObjectUrl(match.osmType, match.osmId))}
                        >
                          <Text style={styles.osmDebugLink}>
                            {`${match.osmType}/${match.osmId} · ${match.name || '(unnamed)'} · ` +
                              `${hasCoordinate(match) ? formatLatLon({ lat: match.lat as number, lon: match.lon as number }) : 'no coordinate'} · ` +
                              `${Math.round(match.distanceMeters)} m · ` +
                              `${match.matchSource ?? 'source unknown'} · ` +
                              `${match.locality ?? 'no locality'} ↗`}
                          </Text>
                        </Pressable>
                      ))}
                      {curl ? renderOsmDebugCommand(`${entry.id}-curl`, curl) : null}
                      {/* The Overpass query the service actually ran. Running it is what settles
                          "OSM has the holes, why does the import not see them": the same query
                          returning nothing is a query problem, returning holes is ours. */}
                      {entry.overpassQuery
                        ? renderOsmDebugCommand(
                            `${entry.id}-overpass`,
                            curlForOverpass(entry.overpassQuery)
                          )
                        : null}
                    </View>
                  );
                })}
              </>
            )}
          </ScrollView>
        ) : null}
      </>
    );
  };

  /** One shell command, selectable, with a copy button where the platform has a clipboard. */
  const renderOsmDebugCommand = (key: string, command: string) => (
    <View key={key} style={styles.osmDebugCommandRow}>
      <Text style={styles.osmDebugCommand} selectable>
        {command}
      </Text>
      {canCopy ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('courseConfig.osmDebugCopy')}
          testID={`${key}-copy`}
          hitSlop={6}
          onPress={() => void navigator.clipboard.writeText(command).catch(() => undefined)}
        >
          <Ionicons name="copy-outline" size={14} color={appColors.text.muted} />
        </Pressable>
      ) : null}
    </View>
  );

  const toggleOsmCategory = (category: keyof OsmSelection) => {
    setOsmState((prev) => ({
      ...prev,
      selection: prev.selection
        ? { ...prev.selection, [category]: !prev.selection[category] }
        : prev.selection,
    }));
  };

  const importOsmCourse = async (match: OsmCourseMatch, selection: OsmSelection) => {
    if (!user?.token) return;
    setOsmState((prev) => ({ ...prev, status: 'importing', message: null }));

    const auth = { headers: { Authorization: `Bearer ${user.token}` } };

    // Remember which OSM element this course is being linked to, so the save records it. A
    // map-picked point (`osmType: 'point'`) is not an OSM element and carries no id to store.
    importedOsmRef.current =
      match.osmType !== 'point' ? { osmType: match.osmType, osmId: match.osmId } : importedOsmRef.current;

    try {
      let holesApplied = 0;
      // Whatever the holes leg wants to say, carried to the end so the summary can say it too.
      let holesNote: string | null = null;
      if (selection.holes) {
        const body = await fetchOsmLogged<OsmHolesResponse>('holes', osmSubUrl(match, 'holes'), (holes) => [
          `${holes.holes?.length ?? 0} hole(s) · total par ${holes.totalPar ?? 'none'}`,
          `warnings: ${holes.warnings?.length ? holes.warnings.join(', ') : 'none'}`,
        ]);
        if (!body) {
          setOsmState((prev) => ({ ...prev, status: 'error', message: t('courseConfig.osmFailed') }));
          return;
        }
        const applied = applyOsmHoles(body);
        holesApplied = applied.count;
        holesNote = applied.note;
      }

      let teeNames: string[] = [];
      let features: OsmFeatureImport[] = [];
      let centrelines: OsmHoleCentreline[] = [];
      if (needsGeometry(selection)) {
        const geometry = await fetchOsmLogged<OsmGeometryResponse>(
          'geometry',
          osmSubUrl(match, 'geometry'),
          (body) => [
            `${body.features?.length ?? 0} feature(s) · ` +
              `${body.features?.filter((f) => f.kind === 'green').length ?? 0} green, ` +
              `${body.features?.filter((f) => f.kind === 'tee').length ?? 0} tee · ` +
              `tee names ${body.teeNames?.length ?? 0} · ` +
              `${body.centrelines?.length ?? 0} centreline(s)`,
            `warnings: ${body.warnings?.length ? body.warnings.join(', ') : 'none'}`,
          ]
        );
        if (!geometry) {
          setOsmState((prev) => ({ ...prev, status: 'error', message: t('courseConfig.osmFailed') }));
          return;
        }
        teeNames = geometry.teeNames ?? [];
        features = geometry.features ?? [];
        centrelines = geometry.centrelines ?? [];
      }

      // A second request, deliberately. Boundaries are an order of magnitude larger than the
      // centre points above, so they are fetched only for the tick that asks for them —
      // see OverpassQueryBuilder.TerrainInCourse.
      // Carried to the summary like holesNote, below — an empty terrain result otherwise looks
      // identical to a course with nothing drawn, when the actual cause is a missing hole ref.
      let terrainNote: string | null = null;
      if (needsTerrain(selection)) {
        const terrain = await fetchOsmLogged<OsmTerrainResponse>(
          'terrain',
          osmSubUrl(match, 'terrain'),
          (body) => [
            `${body.features?.length ?? 0} boundary(ies) · ` +
              `${body.features?.filter((f) => f.kind === 'bunker').length ?? 0} bunker, ` +
              `${body.features?.filter((f) => f.kind === 'water').length ?? 0} water, ` +
              `${body.features?.filter((f) => f.kind === 'fairway').length ?? 0} fairway`,
            `warnings: ${body.warnings?.length ? body.warnings.join(', ') : 'none'}`,
          ]
        );

        // Not fatal, unlike the geometry leg. The holes, tees and greens on this import are
        // already worth having, and terrain is the part of an OSM course least likely to be
        // mapped at all — failing the whole import over it would be the wrong trade.
        if (terrain?.features?.length) {
          features = [...features, ...terrain.features];
        } else if (terrainSkippedNoHoles(terrain?.warnings)) {
          terrainNote = t('courseConfig.osmTerrainNoHoles');
        }
      }

      if (selection.tees && teeNames.length > 0) {
        applyOsmTeeNames(teeNames);
      }

      // Positions need a course id, which a course being created does not have yet. Stage
      // them; the golfCourse.changed handler writes them once the save comes back with an id.
      const wanted = featuresToImport(features, selection);
      pendingGeometryRef.current = wanted.length > 0 ? wanted : null;
      pendingCentrelinesRef.current = centrelines.length > 0 ? centrelines : null;
      if (wanted.length > 0 && editingCourseId) {
        await flushPendingGeometry(editingCourseId);
      }

      // The coordinate goes into the form first: that is what carries it into the save, and
      // so onto the club record. The geodata write is staged the same way the geometry is.
      const positionImported = selection.position && hasCoordinate(match);
      if (positionImported) {
        const position = { lat: match.lat as number, lon: match.lon as number };
        clubCoordsRef.current = position;
        setCourseCoords(position);
        setDisplayCoords(position);
        pendingPositionRef.current = position;
        if (editingCourseId) {
          await flushPendingPosition(editingCourseId);
        }
      }

      const teesApplied = selection.tees ? teeNames.length : 0;
      // A coordinate-only import gets its own line. "Imported 0 holes, 0 tee sets and 0
      // positions" is a true sentence that reads like a failure, which is the wrong thing to
      // say about the import that just unlocked imagery analysis for this course.
      const summaryKey = !positionImported
        ? 'courseConfig.osmImportSummary'
        : holesApplied === 0 && teesApplied === 0 && wanted.length === 0
          ? 'courseConfig.osmImportSummaryPositionOnly'
          : 'courseConfig.osmImportSummaryWithPosition';

      const summary = t(summaryKey, {
        holes: holesApplied,
        tees: teesApplied,
        positions: wanted.length,
      });

      // Both notes explain a category that came back emptier than the counts step promised;
      // neither is fatal on its own, so both can appear together on an otherwise-successful import.
      const notes = [holesNote, terrainNote].filter((note): note is string => Boolean(note));

      setOsmState({
        // A note about the holes is the reason an import looks emptier than expected, so it
        // survives to the end and reads as an error when nothing at all came of the holes.
        status: holesNote && holesApplied === 0 ? 'error' : 'idle',
        message: notes.length > 0 ? `${summary}\n\n${notes.join('\n\n')}` : summary,
      });
    } catch (error) {
      console.warn('[osm] import failed', error);
      setOsmState((prev) => ({ ...prev, status: 'error', message: t('courseConfig.osmFailed') }));
    }
  };

  const sendScan = async (base64: string, mediaType: string) => {
    if (!user?.token) return;
    setScanState({ status: 'scanning', message: t('courseConfig.scanRunning') });
    try {
      const response = await fetch(`${apiBaseUrl}/api/courses/scan-scorecard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
        body: JSON.stringify({ imageBase64: base64, mediaType }),
      });
      if (!response.ok) {
        let message = t('courseConfig.scanFailed');
        let detail: unknown = null;
        try {
          detail = await response.json();
          if ((detail as { error?: string })?.error === 'not_configured') {
            message = t('courseConfig.scanNotConfigured');
          }
        } catch {
          // non-JSON error body
        }
        console.warn('[scorecard] scan failed', response.status, detail);
        setScanState({ status: 'error', message });
        return;
      }
      const data = (await response.json()) as ScanResponse;
      applyScan(data);
    } catch (error) {
      console.warn('[scorecard] scan error', error);
      setScanState({ status: 'error', message: t('courseConfig.scanFailed') });
    }
  };

  const closeCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraOpen(false);
  };

  // Grab the current camera frame as a JPEG and send it to be scanned.
  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    const base64 = dataUrl.includes(',') ? dataUrl.slice(dataUrl.indexOf(',') + 1) : dataUrl;
    closeCamera();
    if (base64) {
      void sendScan(base64, 'image/jpeg');
    }
  };

  const handleNativeScan = async () => {
    const permission = await ImagePicker.requestCameraPermissionsAsync();
    if (!permission.granted) {
      setScanState({ status: 'error', message: t('courseConfig.scanPermissionDenied') });
      return;
    }
    const result = await ImagePicker.launchCameraAsync({ base64: true, quality: 0.85, allowsEditing: false });
    if (result.canceled || result.assets.length === 0) {
      return;
    }
    const asset = result.assets[0];
    if (!asset.base64) {
      setScanState({ status: 'error', message: t('courseConfig.scanFailed') });
      return;
    }
    void sendScan(asset.base64, asset.mimeType ?? 'image/jpeg');
  };

  const onScanPress = () => {
    if (scanState.status === 'scanning' || hydratingCourse) return;
    if (Platform.OS === 'web') {
      setCameraOpen(true);
    } else {
      void handleNativeScan();
    }
  };

  const genderOptions = [
    { value: 'male', label: t('courseConfig.genderMale') },
    { value: 'female', label: t('courseConfig.genderFemale') },
    { value: 'unisex', label: t('courseConfig.genderUnisex') },
  ] as const;

  const resolveGenderLabel = (value: string) => {
    return genderOptions.find((option) => option.value === value)?.label ?? t('courseConfig.genderMale');
  };

  return (
    <ScrollView contentContainerStyle={styles.container} keyboardShouldPersistTaps="handled">
      <View style={styles.card}>
        <Text style={styles.title}>{t('courseConfig.title')}</Text>
        {manageMode ? (
          <View style={styles.manageSelectorGroup}>
            <Text style={styles.label}>{t('courseConfig.manageSelectorLabel')}</Text>
            <Pressable
              testID="course-manage-selector"
              style={styles.genderDropdownButton}
              onPress={() => {
                setCourseSearch('');
                setCourseSelectorOpen(true);
              }}
            >
              <Text style={styles.genderDropdownValue}>
                {editingCourseId
                  ? courseName.trim() || t('courseConfig.manageSelectedCourse')
                  : t('courseConfig.manageCreateNew')}
              </Text>
              <Text style={styles.genderDropdownChevron}>▾</Text>
            </Pressable>
          </View>
        ) : null}
        <View style={styles.inputRow}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>{t('courseConfig.clubLabel')}</Text>
            <TextInput
              testID="course-club-input"
              style={styles.input}
              value={clubName}
              onChangeText={setClubName}
              onBlur={() => {
                // Default the course name to the club name when the user leaves it blank.
                if (clubName.trim() && !courseName.trim()) {
                  setCourseName(clubName.trim());
                }
              }}
              placeholder={t('courseConfig.clubPlaceholder')}
              placeholderTextColor={appColors.text.placeholder}
            />
          </View>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>{t('courseConfig.cityLabel')}</Text>
            <TextInput
              testID="course-city-input"
              style={styles.input}
              value={city}
              onChangeText={setCity}
              placeholder={t('courseConfig.cityPlaceholder')}
              placeholderTextColor={appColors.text.placeholder}
            />
          </View>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>{t('courseConfig.courseLabel')}</Text>
            <TextInput
              testID="course-name-input"
              style={styles.input}
              value={courseName}
              onChangeText={setCourseName}
              placeholder={t('courseConfig.coursePlaceholder')}
              placeholderTextColor={appColors.text.placeholder}
            />
          </View>
        </View>
        {/* A property of the club, not of the course, so it sits with club name and city rather
            than with the holes. On its own row because a URL needs the width. */}
        <View style={styles.inputRow}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>{t('courseConfig.clubWebsiteLabel')}</Text>
            <TextInput
              testID="course-club-website-input"
              style={styles.input}
              value={clubWebsiteUrl}
              onChangeText={setClubWebsiteUrl}
              placeholder={t('courseConfig.clubWebsitePlaceholder')}
              placeholderTextColor={appColors.text.placeholder}
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="url"
            />
          </View>
        </View>
        {displayCoords ? (
          <Text testID="course-coordinates" style={styles.statusText}>
            {t('courseConfig.coordinatesLabel')}: {formatLatLon(displayCoords)}
          </Text>
        ) : null}
        {/* Admin-only: a player adding their home course has no way to judge what an OSM import
            would bring in, so the button reads as a step they have to take and confuses more than
            it helps. Sits directly under the course name for the admins who do see it, because
            naming the course is what makes the import searchable — anything further down the form
            is found too late to save the typing. */}
        {isAdmin ? (
          <>
            <Pressable
              testID="course-osm-import"
              style={[styles.scanButton, hydratingCourse && styles.actionButtonDisabled]}
              onPress={openOsmModal}
              disabled={hydratingCourse}
            >
              <Ionicons name="map" size={18} color={appColors.text.primary} />
              <Text style={styles.scanButtonText}>{t('courseConfig.osmButton')}</Text>
            </Pressable>
            {osmState.message && !osmState.modalOpen ? (
              <Text
                testID="course-osm-status"
                style={osmState.status === 'error' ? styles.errorText : styles.statusText}
              >
                {osmState.message}
              </Text>
            ) : null}
          </>
        ) : null}
        {/* The import modal closes when the import finishes, and takes the record of what it
            did with it — which is exactly when "OSM has no holes for this course" needs
            explaining. So the log is reachable from the screen too. */}
        {renderOsmDebugPanel('screen')}
        <Text style={styles.osmAttribution}>{t('courseConfig.osmAttribution')}</Text>
        <View style={styles.scanRow}>
          <Pressable
            style={[styles.scanButton, (scanState.status === 'scanning' || hydratingCourse) && styles.actionButtonDisabled]}
            onPress={onScanPress}
            disabled={scanState.status === 'scanning' || hydratingCourse}
          >
            {scanState.status === 'scanning' ? (
              <ActivityIndicator color={appColors.text.primary} />
            ) : (
              <Ionicons name="camera" size={18} color={appColors.text.primary} />
            )}
            <Text style={styles.scanButtonText}>
              {scanState.status === 'scanning'
                ? `${t('courseConfig.scanRunning')} ${scanElapsed}s`
                : t('courseConfig.scanButton')}
            </Text>
          </Pressable>
          {scanState.status === 'scanning' ? (
            <Text style={styles.statusText}>{t('courseConfig.scanThinkingHint')}</Text>
          ) : scanState.message ? (
            <Text style={scanState.status === 'error' ? styles.errorText : styles.statusText}>{scanState.message}</Text>
          ) : null}
        </View>
        <View style={styles.inputRow}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>{t('courseConfig.teeParLabel')}</Text>
            <TextInput style={styles.input} value={teePar} onChangeText={setTeePar} keyboardType="number-pad" />
          </View>
        </View>
        <Pressable
          style={styles.officialRow}
          onPress={() => setIsOfficial((prev) => !prev)}
          accessibilityRole="checkbox"
          accessibilityState={{ checked: isOfficial }}
        >
          <View style={[styles.checkbox, isOfficial && styles.checkboxChecked]}>
            {isOfficial ? <Text style={styles.checkboxMark}>✓</Text> : null}
          </View>
          <View style={styles.officialTextGroup}>
            <Text style={styles.officialLabel}>{t('courseConfig.makeOfficialLabel')}</Text>
            <Text style={styles.officialHint}>{t('courseConfig.makeOfficialHint')}</Text>
          </View>
        </Pressable>
        {manageMode ? (
          <Pressable
            style={styles.officialRow}
            onPress={() => setIsDisabled((prev) => !prev)}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: isDisabled }}
            accessibilityLabel={t('courseConfig.disableCourseLabel')}
          >
            <View style={[styles.checkbox, isDisabled && styles.checkboxDanger]}>
              {isDisabled ? <Text style={styles.checkboxMark}>✓</Text> : null}
            </View>
            <View style={styles.officialTextGroup}>
              <Text style={styles.officialLabel}>{t('courseConfig.disableCourseLabel')}</Text>
              <Text style={styles.officialHint}>{t('courseConfig.disableCourseHint')}</Text>
            </View>
          </Pressable>
        ) : null}
        <View style={styles.teeHeaderRow}>
          <Text style={styles.teeSectionTitle}>{t('courseConfig.teesTitle')}</Text>
          <Pressable testID="course-add-tee" style={styles.addTeeButton} onPress={addTee}>
            <Text style={styles.addTeeButtonText}>{t('courseConfig.addTeeButton')}</Text>
          </Pressable>
        </View>
        {tees.map((tee, index) => (
          <View key={tee.id} style={styles.teeCard}>
            <View style={styles.teeCardTopRow}>
              <TextInput
                testID={`course-tee-name-${tee.id}`}
                style={styles.teeTitleInput}
                value={tee.name}
                onChangeText={(value) => updateTee(tee.id, { name: value })}
                placeholder={t('courseConfig.teeLabel', { index: index + 1 })}
                placeholderTextColor={appColors.text.placeholder}
              />
              <Pressable
                style={[styles.removeTeeButton, tees.length <= 1 && styles.removeTeeButtonDisabled]}
                onPress={() => removeTee(tee.id)}
                disabled={tees.length <= 1}
              >
                <Text style={styles.removeTeeButtonText}>{t('courseConfig.removeTeeButton')}</Text>
              </Pressable>
            </View>
            <View style={styles.teeInputsRow}>
              <View style={[styles.teeInputGroup, styles.teeInputGroupGender]}>
                <Text style={[styles.label, styles.teeFieldLabel]}>{t('courseConfig.teeGenderLabel')}</Text>
                <Pressable style={styles.genderDropdownButton} onPress={() => setGenderPickerTeeId(tee.id)}>
                  <Text style={[styles.genderDropdownValue, styles.teeFieldValue]}>{resolveGenderLabel(tee.sex)}</Text>
                  <Text style={styles.genderDropdownChevron}>v</Text>
                </Pressable>
              </View>
              <View style={[styles.teeInputGroup, styles.teeInputGroupRating]}>
                <Text style={[styles.label, styles.teeFieldLabel]}>{t('courseConfig.courseRatingLabel')}</Text>
                <TextInput
                  style={[styles.input, styles.teeFieldInput]}
                  value={tee.courseRating}
                  onChangeText={(value) => updateTee(tee.id, { courseRating: value })}
                  keyboardType="decimal-pad"
                />
              </View>
              <View style={[styles.teeInputGroup, styles.teeInputGroupSlope]}>
                <Text style={[styles.label, styles.teeFieldLabel]}>{t('courseConfig.slopeRatingLabel')}</Text>
                <TextInput
                  style={[styles.input, styles.teeFieldInput]}
                  value={tee.slopeRating}
                  onChangeText={(value) => updateTee(tee.id, { slopeRating: value })}
                  keyboardType="number-pad"
                />
              </View>
            </View>
          </View>
        ))}
        {/* Header and rows share one horizontal scroller so the columns stay lined up: a
            per-row scroller would let them drift apart, and a course with five tees is wider
            than a phone. Nothing scrolls when it already fits. */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={tees.length > 0}
          contentContainerStyle={styles.tableScroller}
        >
        <View>
        <View style={styles.tableHeader}>
          <Text style={[styles.tableCell, styles.holeCell]}>{t('courseConfig.holeHeader')}</Text>
          <Text style={[styles.tableCell, styles.parCell]}>{t('courseConfig.parHeader')}</Text>
          <Text style={[styles.tableCell, styles.strokeCell]} numberOfLines={1} ellipsizeMode="tail">
            {t('courseConfig.strokeIndexHeader')}
          </Text>
          <Text style={[styles.tableCell, styles.lengthCell]} numberOfLines={1} ellipsizeMode="tail">
            {t('courseConfig.lengthHeader')}
          </Text>
          {/* One column per tee, in a strip of their own: five tees would otherwise squeeze the
              four fixed columns off a phone. The header names the tee; an unnamed one shows a
              dash rather than an empty column nobody can identify. */}
          {tees.map((tee) => (
            <Text
              key={`tee-length-header-${tee.id}`}
              style={[styles.tableCell, styles.teeLengthCell]}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {tee.name.trim() || '–'}
            </Text>
          ))}
        </View>
        {holes.map((hole, index) => (
          <View key={hole.number} style={styles.tableRow}>
            <Text style={[styles.tableCell, styles.holeCell]}>{hole.number}</Text>
            <TextInput
              testID={`course-hole-par-${hole.number}`}
              style={[styles.tableCell, styles.parCell, styles.tableInput]}
              value={hole.par}
              onChangeText={(value) => updateHole(index, { par: value })}
              keyboardType="number-pad"
            />
            <TextInput
              testID={`course-hole-stroke-index-${hole.number}`}
              style={[styles.tableCell, styles.strokeCell, styles.tableInput]}
              value={hole.strokeIndex}
              onChangeText={(value) => updateHole(index, { strokeIndex: value })}
              keyboardType="number-pad"
            />
            <TextInput
              testID={`course-hole-length-${hole.number}`}
              style={[styles.tableCell, styles.lengthCell, styles.tableInput]}
              value={hole.lengthMeters}
              onChangeText={(value) => updateHole(index, { lengthMeters: value })}
              keyboardType="number-pad"
              placeholder="–"
              placeholderTextColor={appColors.text.muted}
            />
            {tees.map((tee) => (
              <TextInput
                key={`tee-length-${hole.number}-${tee.id}`}
                testID={`course-hole-tee-length-${hole.number}-${tee.id}`}
                style={[styles.tableCell, styles.teeLengthCell, styles.tableInput]}
                value={hole.teeLengths[tee.id] ?? ''}
                onChangeText={(value) => updateHoleTeeLength(index, tee.id, value)}
                keyboardType="number-pad"
                placeholder="–"
                placeholderTextColor={appColors.text.muted}
              />
            ))}
          </View>
        ))}
        </View>
        </ScrollView>
        {hasBlockingIssues ? (
          <View testID="course-blocking-issues" style={styles.issuesBox}>
            <Text style={styles.issuesTitle}>{t('courseConfig.fixBeforeSave')}</Text>
            {blockingIssues.map((issue, index) => (
              <Text key={index} style={styles.issueText}>
                {'• '}
                {issue}
              </Text>
            ))}
          </View>
        ) : null}
        {hasBlockingIssues ? (
          <Pressable
            style={styles.officialRow}
            onPress={() => setAllowSaveWithErrors((prev) => !prev)}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: allowSaveWithErrors }}
          >
            <View style={[styles.checkbox, allowSaveWithErrors && styles.checkboxChecked]}>
              {allowSaveWithErrors ? <Text style={styles.checkboxMark}>✓</Text> : null}
            </View>
            <Text style={styles.officialLabel}>{t('courseConfig.saveWithErrorsLabel')}</Text>
          </Pressable>
        ) : null}
        <View style={styles.actionRow}>
          <Pressable
            accessibilityRole="button"
            style={[styles.cancelButton, saveState.status === 'saving' && styles.actionButtonDisabled]}
            onPress={() => router.back()}
            disabled={saveState.status === 'saving'}
          >
            <Text style={styles.cancelButtonText}>{t('courseConfig.cancelButton')}</Text>
          </Pressable>
          <Pressable
            testID="course-save"
            // Without an explicit role the rendered <div> carries aria-disabled that nothing
            // honours — neither a screen reader nor Playwright's toBeDisabled().
            accessibilityRole="button"
            style={[styles.saveButton, (saveState.status === 'saving' || hydratingCourse || !clubName.trim() || (hasBlockingIssues && !allowSaveWithErrors)) && styles.actionButtonDisabled]}
            onPress={onSave}
            disabled={saveState.status === 'saving' || hydratingCourse || !clubName.trim() || (hasBlockingIssues && !allowSaveWithErrors)}
          >
            {saveState.status === 'saving' || hydratingCourse ? <ActivityIndicator color={appColors.text.primary} /> : null}
            <Text style={styles.saveButtonText}>{t('courseConfig.saveButton')}</Text>
          </Pressable>
        </View>
        {saveState.message ? (
          <Text testID="course-save-status" style={saveState.status === 'error' ? styles.errorText : styles.statusText}>{saveState.message}</Text>
        ) : null}
      </View>
      <Modal visible={courseSelectorOpen} transparent animationType="fade" onRequestClose={() => setCourseSelectorOpen(false)}>
        <Pressable style={styles.pickerOverlay} onPress={() => setCourseSelectorOpen(false)}>
          {/* Swallow presses inside the card: without this a tap on the search field's padding
              closes the whole picker. */}
          <Pressable style={styles.pickerCard} onPress={(event) => event.stopPropagation()}>
            <View style={styles.manageSearchRow}>
              <Ionicons name="search" size={16} color={appColors.text.muted} />
              <TextInput
                testID="course-manage-search"
                style={styles.manageSearchInput}
                value={courseSearch}
                onChangeText={setCourseSearch}
                placeholder={t('courseConfig.manageSearchPlaceholder')}
                placeholderTextColor={appColors.text.placeholder}
                autoCorrect={false}
                autoCapitalize="none"
                returnKeyType="search"
              />
              {courseSearch.length > 0 ? (
                <Pressable
                  testID="course-manage-search-clear"
                  accessibilityRole="button"
                  onPress={() => setCourseSearch('')}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Ionicons name="close-circle" size={16} color={appColors.text.muted} />
                </Pressable>
              ) : null}
            </View>
            <ScrollView style={styles.manageCourseList} keyboardShouldPersistTaps="handled">
              <Pressable style={styles.pickerOption} onPress={handleCreateNewCourse}>
                <Text style={styles.pickerOptionText}>＋ {t('courseConfig.manageCreateNew')}</Text>
              </Pressable>
              {filteredManageableCourses.map((course) => (
                <Pressable key={course.id} style={styles.pickerOption} onPress={() => handleSelectManagedCourse(course)}>
                  <Text style={styles.pickerOptionText}>
                    {course.name}
                    {course.clubName ? ` · ${course.clubName}` : ''}
                  </Text>
                  {course.city || (course.visibility && course.visibility !== 'official') ? (
                    <Text style={styles.manageCourseMeta}>
                      {[course.city, course.visibility !== 'official' ? course.visibility : '']
                        .filter(Boolean)
                        .join(' · ')}
                    </Text>
                  ) : null}
                </Pressable>
              ))}
              {filteredManageableCourses.length === 0 ? (
                <View style={styles.pickerOption}>
                  <Text testID="course-manage-empty" style={styles.pickerOptionText}>
                    {manageableCourses.length === 0
                      ? t('courseConfig.manageNoCourses')
                      : t('courseConfig.manageNoMatches', { query: courseSearch.trim() })}
                  </Text>
                </View>
              ) : null}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
      {/* OpenStreetMap import, in two steps: find the course, then choose what to take from it.
          The user picks the course rather than the app guessing — a club can have several
          (Österlens has two) and proximity alone lands on the wrong one. The per-category counts
          are shown before anything is imported, so "0 greens" explains itself. */}
      <Modal
        visible={osmState.modalOpen === true}
        transparent
        animationType="fade"
        onRequestClose={closeOsmModal}
      >
        <Pressable style={[styles.pickerOverlay, styles.osmModalOverlay]} onPress={closeOsmModal}>
          <Pressable style={styles.osmModalCard} onPress={(event) => event.stopPropagation()}>
            <View style={styles.osmModalHeader}>
              <Text style={styles.osmPickerTitle}>{t('courseConfig.osmButton')}</Text>
              <Pressable
                testID="course-osm-close"
                accessibilityRole="button"
                accessibilityLabel={t('menu.close')}
                hitSlop={8}
                style={styles.osmModalClose}
                onPress={closeOsmModal}
              >
                <Ionicons name="close" size={20} color={appColors.text.primary} />
              </Pressable>
            </View>
            <View style={styles.osmModalBody}>

            {osmState.pickerOpen ? (
              <LocationPickerMap
                initialCenter={
                  osmState.anchor ??
                  osmState.mapAnchor ??
                  clubCoordsRef.current ??
                  null
                }
                onConfirm={confirmLocationPicker}
                onUseAsPosition={selectPickedLocation}
                onCancel={cancelLocationPicker}
                apiBaseUrl={apiBaseUrl}
                token={user?.token}
              />
            ) : osmState.selected ? (
              renderOsmCategoryStep(osmState.selected)
            ) : (
              <>
                <View style={styles.osmModeRow}>
                  {(['nearby', 'name'] as const).map((mode) => (
                    <Pressable
                      key={mode}
                      testID={`course-osm-mode-${mode}`}
                      accessibilityRole="button"
                      style={[
                        styles.osmModeChip,
                        (osmState.mode ?? 'nearby') === mode && styles.osmModeChipActive,
                      ]}
                      onPress={() =>
                        setOsmState((prev) => ({ ...prev, mode, matches: undefined, message: null }))
                      }
                    >
                      <Text style={styles.osmModeChipText}>
                        {t(mode === 'nearby' ? 'courseConfig.osmModeNearby' : 'courseConfig.osmModeName')}
                      </Text>
                    </Pressable>
                  ))}
                </View>

                {(osmState.mode ?? 'nearby') === 'name' ? (
                  <TextInput
                    testID="course-osm-name-input"
                    style={styles.input}
                    value={osmNameQuery}
                    onChangeText={setOsmNameQuery}
                    placeholder={t('courseConfig.osmNamePlaceholder')}
                    placeholderTextColor={appColors.text.placeholder}
                    onSubmitEditing={() => void searchOsmCourses('name', osmNameQuery)}
                    returnKeyType="search"
                  />
                ) : null}

                <Pressable
                  testID="course-osm-search"
                  style={[styles.scanButton, osmState.status === 'searching' && styles.actionButtonDisabled]}
                  disabled={osmState.status === 'searching'}
                  onPress={() => void searchOsmCourses(osmState.mode ?? 'nearby', osmNameQuery)}
                >
                  {osmState.status === 'searching' ? (
                    <ActivityIndicator color={appColors.text.primary} />
                  ) : (
                    <Ionicons name="search" size={18} color={appColors.text.primary} />
                  )}
                  <Text style={styles.scanButtonText}>
                    {osmState.status === 'searching'
                      ? t('courseConfig.osmSearching')
                      : t('courseConfig.osmSearch')}
                  </Text>
                </Pressable>

                {osmState.message ? (
                  <Text
                    testID="course-osm-modal-status"
                    style={osmState.status === 'error' ? styles.errorText : styles.statusText}
                  >
                    {osmState.message}
                  </Text>
                ) : null}

                <Pressable
                  testID="course-osm-pick-location"
                  accessibilityRole="button"
                  style={styles.osmMapPickerLink}
                  onPress={openLocationPicker}
                >
                  <Ionicons name="location-outline" size={14} color={appColors.accent.primary} />
                  <Text style={styles.osmMapPickerLinkText}>
                    {t('courseConfig.osmPickLocation')}
                  </Text>
                </Pressable>

                {/* Only meaningful once a point has actually been confirmed on the map — a
                    course whose OSM matches are all wrong (or empty) is exactly the case a
                    node-only mapping like Öijared produces: real in OSM, but not a way/relation
                    the golf-course search can use. This is the way out of that dead end. */}
                {osmState.mapAnchor ? (
                  <Pressable
                    testID="course-osm-use-picked-anyway"
                    accessibilityRole="button"
                    style={styles.osmMapPickerLink}
                    onPress={() => {
                      if (osmState.mapAnchor) void selectPickedLocation(osmState.mapAnchor);
                    }}
                  >
                    <Ionicons name="checkmark-circle-outline" size={14} color={appColors.accent.primary} />
                    <Text style={styles.osmMapPickerLinkText}>
                      {t('courseConfig.osmUsePickedAnyway')}
                    </Text>
                  </Pressable>
                ) : null}

                {renderOsmDebugPanel('modal')}

                <ScrollView style={styles.manageCourseList} keyboardShouldPersistTaps="handled">
                  {(osmState.matches ?? []).map((match) => {
                    const counts = countsOf(match);
                    return (
                      <Pressable
                        key={`${match.osmType}-${match.osmId}`}
                        testID={`course-osm-match-${match.osmId}`}
                        style={styles.pickerOption}
                        onPress={() => void selectOsmCandidate(match)}
                      >
                        <Text style={styles.pickerOptionText}>
                          {match.name || t('courseConfig.osmUnnamed')}
                        </Text>
                        {/* Where OSM says the course is, which separates two clubs of the same
                            name. Only a Nominatim hit carries it — a match found by the radius
                            fallback has no address, and shows the coordinates alone. */}
                        {match.locality ? (
                          <Text style={styles.osmDetailText}>{match.locality}</Text>
                        ) : null}
                        {/* Both coordinates, not just the gap between them. A bare "12.4 km" is
                            unreadable: it could be a course 12 km away, or this very course
                            against a stored position that is 12 km wrong, and the two call for
                            opposite actions. Naming the origin — the club's saved position or
                            the phone's — is what tells them apart. */}
                        <Text
                          testID={`course-osm-match-coords-${match.osmId}`}
                          style={styles.osmDetailText}
                        >
                          {osmMatchCoordinateLine(match)}
                        </Text>
                      </Pressable>
                    );
                  })}
                </ScrollView>
              </>
            )}
            </View>
          </Pressable>
        </Pressable>
      </Modal>
      <Modal visible={genderPickerTeeId !== null} transparent animationType="fade" onRequestClose={() => setGenderPickerTeeId(null)}>
        <Pressable style={styles.pickerOverlay} onPress={() => setGenderPickerTeeId(null)}>
          <View style={styles.pickerCard}>
            {genderOptions.map((option) => (
              <Pressable
                key={option.value}
                style={styles.pickerOption}
                onPress={() => {
                  if (genderPickerTeeId) {
                    updateTee(genderPickerTeeId, { sex: option.value });
                  }
                  setGenderPickerTeeId(null);
                }}
              >
                <Text style={styles.pickerOptionText}>{option.label}</Text>
              </Pressable>
            ))}
          </View>
        </Pressable>
      </Modal>
      {Platform.OS === 'web' ? (
        <Modal visible={cameraOpen} transparent animationType="fade" onRequestClose={closeCamera}>
          <View style={styles.cameraOverlay}>
            <View style={styles.cameraCard}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: '100%', maxHeight: 360, borderRadius: 12, backgroundColor: appColors.neutral.black }}
              />
              <View style={styles.cameraActions}>
                <Pressable style={styles.cameraCancelButton} onPress={closeCamera}>
                  <Text style={styles.cancelButtonText}>{t('courseConfig.cameraCancel')}</Text>
                </Pressable>
                <Pressable style={styles.cameraCaptureButton} onPress={capturePhoto}>
                  <Ionicons name="camera" size={18} color={appColors.text.inverse} />
                  <Text style={styles.saveButtonText}>{t('courseConfig.cameraCapture')}</Text>
                </Pressable>
              </View>
            </View>
          </View>
        </Modal>
      ) : null}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    padding: 20,
    gap: 12,
    backgroundColor: appColors.background.app,
    alignItems: 'center',
  },
  card: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
    padding: 16,
    gap: 12,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  inputRow: {
    gap: 12,
  },
  inputGroup: {
    gap: 6,
  },
  label: {
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    color: appColors.text.primary,
  },
  input: {
    borderWidth: 1,
    borderColor: appColors.border.accent,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 15,
    color: appColors.text.primary,
    backgroundColor: appColors.background.panel,
  },
  tableHeader: {
    flexDirection: 'row',
    paddingVertical: 8,
    columnGap: 12,
    borderBottomWidth: 1,
    borderColor: appColors.border.accent,
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 1,
    columnGap: 12,
    borderBottomWidth: 1,
    borderColor: alpha(appColors.accent.indigo, 0.6),
  },
  tableCell: {
    fontSize: 14,
    color: appColors.text.secondary,
  },
  holeCell: {
    width: 46,
  },
  parCell: {
    width: 62,
  },
  strokeCell: {
    width: 86,
  },
  lengthCell: {
    width: 78,
  },
  // Narrower than the hole's own length column: these are extra detail beside it, and a course
  // with five tees has to stay readable on a laptop without the row wrapping.
  teeLengthCell: {
    width: 66,
  },
  // The scroller must not stretch its content to the viewport, or the table would be squeezed
  // back to the width it is trying to escape.
  tableScroller: {
    flexGrow: 0,
  },
  // The card sits on top of a screen that is itself dark panels, so without a lit border and a
  // shadow it reads as one more section of the page rather than as a window over it.
  osmModalCard: {
    width: '92%',
    maxWidth: 520,
    maxHeight: '85%',
    alignSelf: 'center',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.elevated,
    overflow: 'hidden',
    shadowColor: appColors.neutral.black,
    shadowOpacity: 0.45,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 12 },
    elevation: 12,
  },
  osmModalOverlay: {
    backgroundColor: alpha(appColors.neutral.black, 0.6),
  },
  osmModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
  },
  osmModalClose: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 16,
  },
  osmModalBody: {
    flexShrink: 1,
    padding: 16,
    gap: 10,
  },
  osmModeRow: {
    flexDirection: 'row',
    gap: 8,
  },
  osmModeChip: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.subtle,
  },
  osmModeChipActive: {
    borderColor: appColors.background.interactive,
    borderWidth: 2,
  },
  osmModeChipText: {
    color: appColors.text.primary,
    fontSize: 14,
  },
  osmCategoryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 8,
  },
  osmCategoryRowDisabled: {
    opacity: 0.45,
  },
  osmCategoryText: {
    flexShrink: 1,
  },
  osmActionRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 10,
    marginTop: 8,
  },
  // scanButton is built as a full-width block and carries no horizontal padding, so in a row
  // its label runs straight into the border. Both buttons share the width instead.
  osmPrimaryButton: {
    flex: 1,
    paddingHorizontal: 16,
  },
  osmSecondaryButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 11,
    paddingHorizontal: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.subtle,
  },
  osmLoadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 20,
  },
  // manageCourseMeta capitalizes every word, which is fine for "18 holes · 300 m away" and
  // wrong for a sentence. These lines are sentences.
  osmDetailText: {
    color: appColors.text.muted,
    fontSize: 12,
    lineHeight: 17,
    flexShrink: 1,
  },
  // A tertiary escape hatch, not a step in the main flow — a text link rather than a button,
  // always available so an admin who already suspects the anchor is wrong doesn't have to
  // search-and-fail first.
  osmMapPickerLink: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
  },
  osmMapPickerLinkText: {
    color: appColors.accent.primary,
    fontSize: 13,
    fontWeight: '600',
  },
  osmPositionChoiceRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    // Indented under the Position row's icon, rather than flush with the category rows above
    // it — this is a detail of that one row, not a category of its own.
    marginLeft: 32,
    marginBottom: 6,
  },
  osmPositionChoiceChip: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.subtle,
  },
  osmPositionChoiceChipActive: {
    borderColor: appColors.accent.primary,
    backgroundColor: alpha(appColors.accent.primary, 0.18),
  },
  osmPositionChoiceText: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '600',
  },
  osmDebugToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 4,
  },
  osmDebugToggleText: {
    color: appColors.text.muted,
    fontSize: 12,
  },
  osmDebugPanel: {
    maxHeight: 220,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.border.hairline,
    padding: 8,
  },
  osmDebugEntry: {
    gap: 2,
    paddingVertical: 6,
  },
  osmDebugHeading: {
    color: appColors.text.primary,
    fontSize: 11,
    fontWeight: '600',
  },
  // Diagnostics, not prose: monospace keeps two coordinates readable against each other, and
  // the size is what fits a full request URL without wrapping three times.
  osmDebugText: {
    color: appColors.text.muted,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    fontSize: 10,
  },
  osmDebugCommandRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
    marginTop: 2,
  },
  // The command has to survive being copied, so it wraps rather than truncates.
  osmDebugCommand: {
    flex: 1,
    color: appColors.text.primary,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    fontSize: 10,
    backgroundColor: alpha(appColors.text.primary, 0.08),
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  osmDebugLink: {
    color: appColors.background.interactive,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    fontSize: 10,
    textDecorationLine: 'underline',
  },
  osmAttribution: {
    color: appColors.text.muted,
    fontSize: 11,
  },
  osmPickerTitle: {
    flex: 1,
    color: appColors.text.primary,
    fontSize: 16,
    fontWeight: '700',
  },
  tableInput: {
    borderWidth: 1,
    borderColor: appColors.border.accent,
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: appColors.background.panel,
    color: appColors.text.primary,
  },
  officialRow: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 12,
    paddingVertical: 4,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: appColors.border.accent,
    backgroundColor: appColors.background.panel,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: appColors.background.interactive,
  },
  checkboxDanger: {
    backgroundColor: appColors.status.danger,
    borderColor: appColors.status.danger,
  },
  checkboxMark: {
    color: appColors.text.inverse,
    fontSize: 15,
    fontWeight: '900',
    lineHeight: 17,
  },
  officialTextGroup: {
    flex: 1,
    gap: 2,
  },
  officialLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  officialHint: {
    fontSize: 12,
    color: appColors.text.secondary,
  },
  scanRow: {
    marginTop: 14,
    gap: 6,
  },
  scanButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    columnGap: 8,
    paddingVertical: 11,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.accent,
    backgroundColor: appColors.background.panelAlt,
  },
  scanButtonText: {
    color: appColors.text.primary,
    fontSize: 15,
    fontWeight: '700',
  },
  cameraOverlay: {
    flex: 1,
    backgroundColor: alpha(appColors.neutral.black, 0.6),
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  cameraCard: {
    width: '100%',
    maxWidth: 480,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
    padding: 12,
    rowGap: 12,
  },
  cameraActions: {
    flexDirection: 'row',
    columnGap: 10,
  },
  cameraCancelButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cameraCaptureButton: {
    flex: 1.2,
    flexDirection: 'row',
    columnGap: 8,
    paddingVertical: 12,
    borderRadius: 12,
    backgroundColor: appColors.background.interactive,
    alignItems: 'center',
    justifyContent: 'center',
  },
  teeHeaderRow: {
    marginTop: 14,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    columnGap: 12,
  },
  teeSectionTitle: {
    fontSize: 14,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: appColors.text.primary,
  },
  addTeeButton: {
    borderWidth: 1,
    borderColor: appColors.border.strong,
    borderRadius: 10,
    backgroundColor: appColors.background.panelAlt,
    paddingHorizontal: 12,
    paddingVertical: 7,
  },
  addTeeButtonText: {
    color: appColors.text.secondary,
    fontSize: 13,
    fontWeight: '700',
  },
  teeCard: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: appColors.border.accent,
    borderRadius: 12,
    backgroundColor: appColors.background.panel,
    padding: 12,
    rowGap: 10,
  },
  teeCardTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    columnGap: 10,
  },
  teeTitleInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: appColors.border.accent,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: appColors.background.panel,
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
    marginRight: 8,
  },
  removeTeeButton: {
    borderWidth: 1,
    borderColor: appColors.status.dangerBg,
    borderRadius: 10,
    backgroundColor: appColors.status.dangerBgStrong,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  removeTeeButtonDisabled: {
    opacity: 0.45,
  },
  removeTeeButtonText: {
    color: appColors.text.secondary,
    fontSize: 12,
    fontWeight: '700',
  },
  teeInputsRow: {
    flexDirection: 'row',
    columnGap: 6,
    flexWrap: 'nowrap',
    alignItems: 'flex-end',
  },
  teeInputGroup: {
    flex: 1,
    gap: 6,
    minWidth: 0,
  },
  teeInputGroupGender: {
    flex: 1.2,
  },
  teeInputGroupRating: {
    flex: 1,
  },
  teeInputGroupSlope: {
    flex: 1,
  },
  genderDropdownButton: {
    borderWidth: 1,
    borderColor: appColors.border.accent,
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: appColors.background.panel,
  },
  manageSelectorGroup: {
    marginBottom: 16,
    gap: 6,
  },
  manageCourseList: {
    maxHeight: 360,
  },
  manageSearchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderColor: alpha(appColors.accent.indigo, 0.6),
  },
  manageSearchInput: {
    flex: 1,
    color: appColors.text.primary,
    fontSize: 15,
    paddingVertical: 4,
  },
  manageCourseMeta: {
    color: appColors.text.muted,
    fontSize: 11,
    marginTop: 2,
    textTransform: 'capitalize',
  },
  genderDropdownValue: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '600',
  },
  genderDropdownChevron: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '600',
  },
  pickerOverlay: {
    flex: 1,
    backgroundColor: alpha(appColors.neutral.black, 0.35),
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  pickerCard: {
    width: '100%',
    maxWidth: 320,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
    overflow: 'hidden',
  },
  pickerOption: {
    paddingVertical: 14,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderColor: alpha(appColors.accent.indigo, 0.6),
  },
  pickerOptionText: {
    color: appColors.text.primary,
    fontSize: 15,
  },
  teeFieldLabel: {
    fontSize: 9,
  },
  teeFieldInput: {
    fontSize: 12,
  },
  teeFieldValue: {
    fontSize: 11,
  },
  actionRow: {
    marginTop: 12,
    flexDirection: 'row',
    columnGap: 10,
  },
  actionButtonDisabled: {
    opacity: 0.5,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  cancelButtonText: {
    fontSize: 15,
    fontWeight: '700',
    color: appColors.text.secondary,
  },
  saveButton: {
    flex: 1.2,
    paddingVertical: 12,
    borderRadius: 12,
    backgroundColor: appColors.background.interactive,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  saveButtonText: {
    fontSize: 15,
    fontWeight: '700',
    color: appColors.text.inverse,
  },
  statusText: {
    color: appColors.text.primary,
  },
  errorText: {
    color: appColors.status.dangerStrong,
  },
  issuesBox: {
    marginTop: 12,
    borderWidth: 1,
    borderColor: appColors.status.dangerBg,
    borderRadius: 12,
    backgroundColor: alpha(appColors.status.dangerBg, 0.18),
    padding: 12,
    rowGap: 6,
  },
  issuesTitle: {
    color: appColors.status.dangerText,
    fontSize: 13,
    fontWeight: '700',
  },
  issueText: {
    color: appColors.status.dangerTextSoft,
    fontSize: 13,
    lineHeight: 18,
  },
});
